<template>
  <NotFoundView
    v-if="personNotFound"
    heading="Person not found"
    message="The person you were looking for could not be found."
  />
  <main v-else :class="['single-person-detail-prototype', 'single-person-prototype', selectedMedium === 'tv' ? 'single-person-prototype--tv' : null]">
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="assets.logo"
        :background-src="heroAssets.background"
        :background-wrap-class="personHeroBackgroundClasses"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
        @background-load="handlePersonHeroBackgroundLoad"
      >
        <div class="row g-0 hero__grid align-items-end">
          <div class="col-auto hero__posterWrap">
            <div
              :class="personPortraitFrameClasses"
              data-testid="person-portrait-frame"
            >
              <div
                v-if="personPortraitLoading"
                class="image-load-frame__skeleton"
                data-testid="person-portrait-skeleton"
                aria-hidden="true"
              ></div>
              <img
                v-if="personPortraitImageSrc"
                :class="personPortraitImageClasses"
                :src="personPortraitImageSrc"
                :alt="`${person.name} portrait`"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                @load="handlePersonPortraitLoad"
                @error="handlePersonPortraitError"
              >
            </div>
          </div>
          <div class="col hero__meta">
            <div class="upper-container">
              <div class="user-event-container">
                <h1 class="item-title" data-testid="person-title">{{ person.name }}</h1>
                <h2 class="jobs" data-testid="person-jobs">{{ person.jobs }}</h2>

                <div v-if="hasLoadedPersonSummary" class="buttons">
                  <template v-if="isAuthenticated">
                    <button
                      class="btn single-btn person-fav-btn"
                      :class="{ 'btn--favourited': isPersonFavourited }"
                      data-testid="person-favourite-action"
                      type="button"
                      :disabled="personFavouritePending"
                      @click="togglePersonFavourite"
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i>
                        <span class="fav-btn">{{ personFavouriteButtonText }}</span>
                        <span class="fav-btn-sm">{{ personFavouriteButtonShortText }}</span>
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a class="btn single-btn person-fav-btn" data-testid="person-favourite-action" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <section class="mobile g-0 container">
      <div class="row g-0">
        <h1 class="item-title-mobile">{{ person.name }}</h1>
        <h2 class="jobs">{{ person.jobs }}</h2>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div v-if="hasLoadedPersonSummary" class="buttons">
            <template v-if="isAuthenticated">
              <button
                class="btn single-btn"
                :class="{ 'btn--favourited': isPersonFavourited }"
                data-testid="person-favourite-action-mobile"
                type="button"
                :disabled="personFavouritePending"
                @click="togglePersonFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> {{ personFavouriteButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="btn single-btn" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Favourite
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
    </section>

    <Transition name="person-data-reveal">
      <div
        v-if="!hasLoadedPersonPayload"
        class="container g-0 person-page-loading"
        data-testid="person-page-loading"
      >
        <p>Loading...</p>
      </div>
    </Transition>

    <Transition name="person-data-reveal">
      <div v-if="hasLoadedPersonPayload" data-testid="person-loaded-content">
        <div class="container g-0 mobile-nav">
          <div class="row">
            <div class="col">
              <nav class="nav">
                <ul class="film-nav">
                  <li>
                    <a :class="{ active: activeSection === 'filmography' }" :href="filmographyPath">
                      Filmography
                    </a>
                  </li>
                  <li>
                    <a :class="{ active: activeSection === 'lists' }" :href="listsPath">
                      Lists
                    </a>
                  </li>
                  <li>
                    <a :class="{ active: activeSection === 'comments' }" :href="commentsPath">
                      Comments
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        <div v-if="activeSection === 'filmography'" class="container g-0">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0">
            <div class="row g-0">
              <div class="col g-0">
                <div class="container g-0">
                  <div class="row g-0">
                    <div class="col g-0 bio">
                      <h2 class="person-filmography-heading" data-testid="person-job-heading">
                        {{ filmographyHeading }}
                      </h2>
                      <div
                        v-if="hasFilmMedium || hasShowMedium"
                        class="person-medium-switcher"
                        data-testid="person-medium-switcher"
                      >
                        <template v-if="hasFilmMedium">
                          <span
                            v-if="selectedMedium === 'film' || !hasShowMedium"
                            class="person-medium-switcher__item person-medium-switcher__item--active"
                            data-testid="person-medium-film"
                          >
                            Films
                          </span>
                          <a
                            v-else
                            class="person-medium-switcher__item"
                            data-testid="person-medium-film"
                            :href="filmMediumPath"
                          >
                            Films
                          </a>
                        </template>
                        <span
                          v-if="hasFilmMedium && hasShowMedium"
                          class="person-medium-switcher__separator"
                          aria-hidden="true"
                        >•</span>
                        <template v-if="hasShowMedium">
                          <span
                            v-if="selectedMedium === 'tv' || !hasFilmMedium"
                            class="person-medium-switcher__item person-medium-switcher__item--active"
                            data-testid="person-medium-tv"
                          >
                            TV
                          </span>
                          <a
                            v-else
                            class="person-medium-switcher__item"
                            data-testid="person-medium-tv"
                            :href="tvMediumPath"
                          >
                            TV
                          </a>
                        </template>
                      </div>
                    </div>
                    <div class="col-3 g-0 related-resources-section">
                      <div class="container person-filters">
                        <div class="row g-0">
                          <div class="col g-0">
                            <h3>Related resources:</h3>
                            <a
                              v-for="resource in relatedResources"
                              :key="resource.label"
                              class="related-link"
                              :href="resource.href"
                            >
                              {{ resource.label }}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row g-0 sorting-buttons-row">
                  <div class="col g-0">
                    <div class="sorting-buttons">
                      <span class="sort-title">Sort:</span>
                      <a
                        v-for="button in sortButtons"
                        :key="button.label"
                        :class="[
                          button.first ? 'first-btn' : null,
                          'sorting-btn',
                          button.active ? 'active' : null,
                        ]"
                        href="#"
                        @click.prevent="setSortMode(button.key)"
                      >
                        {{ button.label }}
                      </a>
                      <a
                        v-for="button in viewModeButtons"
                        :key="`desktop-${button.label}`"
                        :class="[
                          button.first ? 'first-btn' : null,
                          'view-mode-btn',
                          'sorting-btn',
                          button.active ? 'active' : null,
                        ]"
                        data-testid="person-desktop-view-mode-button"
                        href="#"
                        :aria-label="button.label"
                        :title="button.label"
                        @click.prevent="handleViewModeClick(button)"
                      >
                        <ViewModeIcon :mode="button.key" data-testid="person-desktop-view-mode-icon" />
                      </a>
                      <button
                        class="sorting-btn filter-btn"
                        data-testid="person-filter-button"
                        type="button"
                        @click="toggleFilters"
                      >
                        {{ filterButtonLabel }}
                      </button>

                      <div
                        class="modal"
                        data-testid="person-filter-modal"
                        :style="{ display: filtersModalOpen ? 'flex' : 'none' }"
                        @click.self="toggleFilters"
                      >
                        <button
                          class="person-compact-rating-picker__close person-filter-modal__close"
                          data-testid="person-filter-modal-close"
                          type="button"
                          aria-label="Close filters"
                          @click="toggleFilters"
                        >
                          <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
                        </button>
                        <div class="container person-filters">
                          <div
                            class="person-filter-modal__header"
                            data-testid="person-filter-modal-header"
                          >
                            <h2 data-testid="person-filter-modal-title">Filters:</h2>
                            <button
                              class="sorting-btn person-filter-modal__apply person-filter-modal__apply-mobile"
                              data-testid="person-filter-modal-apply"
                              type="button"
                              @click="toggleFilters"
                            >
                              Apply
                            </button>
                          </div>
                          <div
                            v-for="row in filterModalRows"
                            :key="row.key"
                            class="row g-0 filters-groups-row person-filter-modal__row"
                            data-testid="person-filter-modal-row"
                          >
                            <div
                              v-for="group in row.groups"
                              :key="group.title"
                              :class="[
                                'person-filter-modal__group',
                                row.full ? 'person-filter-modal__group--full col-12' : 'person-filter-modal__group--half col-6',
                              ]"
                              data-testid="person-filter-modal-group"
                            >
                              <div class="single-filter-title-row">
                                <h3>{{ group.title }}</h3>
                              </div>
                              <div class="filter-row">
                                <a
                                  v-for="option in group.options"
                                  :key="option.label"
                                  :class="{ active: option.active }"
                                  href="#"
                                  @click.prevent="applyFilter(group.key, option.value)"
                                >
                                  {{ option.label }}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="view-mode-btns">
                        <a
                          v-for="button in viewModeButtons"
                          :key="button.label"
                          :class="[
                            button.first ? 'first-btn' : null,
                            'view-mode-btn',
                            'sorting-btn',
                            button.active ? 'active' : null,
                          ]"
                          href="#"
                          :aria-label="button.label"
                          :title="button.label"
                          @click.prevent="handleViewModeClick(button)"
                        >
                          <ViewModeIcon :mode="button.key" />
                        </a>
                        <button
                          class="sorting-btn filter-btn view-mode-filter-btn"
                          data-testid="person-mobile-filter-button"
                          type="button"
                          @click="toggleFilters"
                        >
                          {{ filterButtonLabel }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row g-0 active-filters-summary-row">
                  <div class="col g-0">
                    <p class="active-filters-summary" data-testid="person-active-filters-summary">
                      {{ activeFiltersSummary }}
                    </p>
                  </div>
                </div>
                <div class="row g-0 filters-section-sm">
                  <div class="row g-0">
                    <div class="col g-0">
                      <div class="container g-0">
                        <div class="row g-0">
                          <div class="col g-0 filtering-row">
                            <input
                              id="person-filmography-filter"
                              v-model="filmographyFilter"
                              class="filmography-filter"
                              type="search"
                              :placeholder="filmographyFilterPlaceholder"
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="container g-0">
              <div class="row g-0">
                <div class="col g-0">
                  <div v-if="currentViewMode === '1' && selectedMedium === 'film'" class="container g-0 credits-list">
                    <p
                      v-if="visibleCredits.length === 0"
                      class="person-no-results"
                      data-testid="person-no-credits"
                    >
                      {{ noCreditsMessage }}
                    </p>
                    <template v-for="(credit, index) in visibleCredits" :key="credit.title">
                      <div class="row g-0 single-credit-row" data-testid="person-credit-row">
                        <div class="col-3 g-0 film-poster-col">
                          <div class="film-poster-container">
                            <a :href="credit.film_url || '#'"><img class="film-poster" :src="credit.poster_url" :alt="`${credit.title} poster`" loading="lazy" decoding="async"></a>
                          </div>
                        </div>
                        <div class="col g-0">
                          <div class="container g-0 single-credit-container">
                            <div class="row g-0 credit-title-row">
                              <div class="col g-0">
                                <a :href="credit.film_url || '#'">
                                  <h3 class="credit-film-title">
                                    {{ credit.title }}
                                    <span v-if="credit.year" class="release-year">({{ credit.year }})</span>
                                  </h3>
                                </a>
                              </div>
                            </div>
                            <div v-if="credit.character_name" class="row g-0 character-name-row">
                              <div class="col g-0">
                                as <span class="character-name">{{ credit.character_name }}</span>
                              </div>
                            </div>
                            <div v-if="credit.overview" class="row g-0 overview-row">
                              <div class="col g-0">
                                <p class="credit-film-overview">
                                  <span data-testid="person-credit-overview-body">{{ displayedCreditOverview(credit) }}</span>
                                  <button
                                    v-if="hasExpandableCreditOverview(credit)"
                                    class="overview-toggle"
                                    data-testid="person-credit-overview-toggle"
                                    type="button"
                                    @click="toggleCreditOverview(credit)"
                                  >
                                    <span class="overview-toggle__icon" aria-hidden="true">{{ isCreditOverviewExpanded(credit) ? '▲' : '▼' }}</span>
                                  </button>
                                </p>
                              </div>
                            </div>
                            <div
                              v-if="credit.directors.length"
                              :class="[
                                'row',
                                'g-0',
                                'directors-row',
                                { 'first-detail-row-spaced': shouldAddDetailsTopGap(credit, 'directors') },
                              ]"
                            >
                              <div class="col g-0">
                                <div class="directors">
                                  <p class="credit-film-runtime">Directed by:</p>
                                  <template v-for="(director, directorIndex) in credit.directors" :key="director.name">
                                    <a :href="director.url || '#'">{{ director.name }}</a>
                                    <span v-if="directorIndex < credit.directors.length - 1"> • </span>
                                  </template>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="credit.stars.length"
                              :class="[
                                'row',
                                'g-0',
                                'stars-row',
                                { 'first-detail-row-spaced': shouldAddDetailsTopGap(credit, 'stars') },
                              ]"
                            >
                              <div class="col g-0">
                                <div class="stars">
                                  <p class="credit-film-runtime">Stars:</p>
                                  <template v-for="(star, starIndex) in credit.stars" :key="star.name">
                                    <a
                                      :data-testid="index === 0 && starIndex === 0 ? 'person-first-stars-link' : null"
                                      :href="star.url || '#'"
                                    >{{ star.name }}</a>
                                    <span v-if="starIndex < credit.stars.length - 1"> • </span>
                                  </template>
                                </div>
                              </div>
                            </div>
                            <div class="row g-0 person-credits-button-row">
                              <div class="col g-0">
                                <div class="person-buttons person-buttons--credit" data-testid="person-credit-action-group">
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn left-btn"
                                      :class="{ 'btn--watched': isCreditWatched(credit) }"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-watch-action"
                                      type="button"
                                      :disabled="isCreditWatchPending(credit)"
                                      @click="markCreditWatched(credit)"
                                    >
                                      <div class="default-btn">
                                        <i :class="creditWatchIconClass(credit)"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="fs-btn left-btn"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-watch-action"
                                      :href="loginPath"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-plus"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn middle-btn"
                                      :class="{ 'btn--watchlisted': isCreditWatchlisted(credit) }"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-watchlist-action"
                                      type="button"
                                      :disabled="isCreditWatchlistPending(credit)"
                                      @click="toggleCreditWatchlist(credit)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="fs-btn middle-btn"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-watchlist-action"
                                      :href="loginPath"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn right-btn"
                                      :class="{ 'btn--favourited': isCreditFavourited(credit) }"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-favourite-action"
                                      type="button"
                                      :disabled="isCreditFavouritePending(credit)"
                                      @click="toggleCreditFavourite(credit)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="fs-btn right-btn"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-favourite-action"
                                      :href="loginPath"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </a>
                                  </template>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="rating-stars">
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="person-credit-rating-summary"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-rating-summary-star"
                                      type="button"
                                      :disabled="filmRatingPendingBySlug[creditFilmSlug(credit) || '']"
                                      @click="openCompactCreditFilmRatingPicker(credit)"
                                    >
                                      <i :class="creditFilmSummaryRatingStarClass(credit)"></i>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="person-credit-rating-summary"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-rating-summary-star"
                                      :href="loginPath"
                                    >
                                      <i class="rating-star ion-ios-star-outline"></i>
                                    </a>
                                  </template>
                                  <div class="current-credits-rating">
                                    <p>{{ creditFilmRatingText(credit) }}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="credit.overview" class="row g-0 overview-row-sm">
                        <div class="col g-0">
                          <p class="credit-film-overview">
                            <span data-testid="person-credit-overview-body">{{ displayedCreditOverview(credit) }}</span>
                            <button
                              v-if="hasExpandableCreditOverview(credit)"
                              class="overview-toggle"
                              data-testid="person-credit-overview-toggle"
                              type="button"
                              @click="toggleCreditOverview(credit)"
                            >
                              <span class="overview-toggle__icon" aria-hidden="true">{{ isCreditOverviewExpanded(credit) ? '▲' : '▼' }}</span>
                            </button>
                          </p>
                        </div>
                      </div>
                      <div
                        v-if="credit.directors.length"
                        :class="[
                          'row',
                          'g-0',
                          'directors-row-sm',
                          { 'first-detail-row-spaced': shouldAddDetailsTopGap(credit, 'directors') },
                        ]"
                      >
                        <div class="col g-0">
                          <div class="directors">
                            <p class="credit-film-runtime">Directed by:</p>
                            <template v-for="(director, directorIndex) in credit.directors" :key="`${credit.title}-${director.name}`">
                              <a :href="director.url || '#'">{{ director.name }}</a>
                              <span v-if="directorIndex < credit.directors.length - 1"> • </span>
                            </template>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="credit.stars.length"
                        :class="[
                          'row',
                          'g-0',
                          'stars-row-sm',
                          { 'first-detail-row-spaced': shouldAddDetailsTopGap(credit, 'stars') },
                        ]"
                      >
                        <div class="col g-0">
                          <div class="stars">
                            <p class="credit-film-runtime">Stars:</p>
                            <template v-for="(star, starIndex) in credit.stars" :key="`${credit.title}-${star.name}`">
                              <a :href="star.url || '#'">{{ star.name }}</a>
                              <span v-if="starIndex < credit.stars.length - 1"> • </span>
                            </template>
                          </div>
                        </div>
                      </div>
                      <div
                        :class="[
                          'row',
                          'g-0',
                          'person-credits-button-row-sm',
                          index === visibleCredits.length - 1 ? 'last-person-credits-button-row-sm' : null,
                        ]"
                      >
                        <div class="col g-0">
                          <div class="person-buttons person-buttons--credit" data-testid="person-credit-action-group">
                            <template v-if="isAuthenticated">
                              <button
                                class="fs-btn left-btn"
                                :class="{ 'btn--watched': isCreditWatched(credit) }"
                                :data-film-slug="creditFilmSlug(credit) || ''"
                                data-testid="person-credit-watch-action"
                                type="button"
                                :disabled="isCreditWatchPending(credit)"
                                @click="markCreditWatched(credit)"
                              >
                                <div class="default-btn">
                                  <i :class="creditWatchIconClass(credit)"></i>
                                </div>
                              </button>
                            </template>
                            <template v-else>
                              <a
                                class="fs-btn left-btn"
                                :data-film-slug="creditFilmSlug(credit) || ''"
                                data-testid="person-credit-watch-action"
                                :href="loginPath"
                              >
                                <div class="default-btn">
                                  <i class="ion-plus"></i>
                                </div>
                              </a>
                            </template>
                            <template v-if="isAuthenticated">
                              <button
                                class="fs-btn middle-btn"
                                :class="{ 'btn--watchlisted': isCreditWatchlisted(credit) }"
                                :data-film-slug="creditFilmSlug(credit) || ''"
                                data-testid="person-credit-watchlist-action"
                                type="button"
                                :disabled="isCreditWatchlistPending(credit)"
                                @click="toggleCreditWatchlist(credit)"
                              >
                                <div class="default-btn">
                                  <i class="ion-bookmark"></i>
                                </div>
                              </button>
                            </template>
                            <template v-else>
                              <a
                                class="fs-btn middle-btn"
                                :data-film-slug="creditFilmSlug(credit) || ''"
                                data-testid="person-credit-watchlist-action"
                                :href="loginPath"
                              >
                                <div class="default-btn">
                                  <i class="ion-bookmark"></i>
                                </div>
                              </a>
                            </template>
                            <template v-if="isAuthenticated">
                              <button
                                class="fs-btn right-btn"
                                :class="{ 'btn--favourited': isCreditFavourited(credit) }"
                                :data-film-slug="creditFilmSlug(credit) || ''"
                                data-testid="person-credit-favourite-action"
                                type="button"
                                :disabled="isCreditFavouritePending(credit)"
                                @click="toggleCreditFavourite(credit)"
                              >
                                <div class="default-btn">
                                  <i class="ion-heart"></i>
                                </div>
                              </button>
                            </template>
                            <template v-else>
                              <a
                                class="fs-btn right-btn"
                                :data-film-slug="creditFilmSlug(credit) || ''"
                                data-testid="person-credit-favourite-action"
                                :href="loginPath"
                              >
                                <div class="default-btn">
                                  <i class="ion-heart"></i>
                                </div>
                              </a>
                            </template>
                          </div>
                        </div>
                        <div class="col g-0">
                          <div class="rating-stars">
                            <template v-if="isAuthenticated">
                              <button
                                class="person-credit-rating-summary"
                                :data-film-slug="creditFilmSlug(credit) || ''"
                                data-testid="person-credit-rating-summary-star"
                                type="button"
                                :disabled="filmRatingPendingBySlug[creditFilmSlug(credit) || '']"
                                @click="openCompactCreditFilmRatingPicker(credit)"
                              >
                                <i :class="creditFilmSummaryRatingStarClass(credit)"></i>
                              </button>
                            </template>
                            <template v-else>
                              <a
                                class="person-credit-rating-summary"
                                :data-film-slug="creditFilmSlug(credit) || ''"
                                data-testid="person-credit-rating-summary-star"
                                :href="loginPath"
                              >
                                <i class="rating-star ion-ios-star-outline"></i>
                              </a>
                            </template>
                            <div class="current-credits-rating">
                              <p>{{ creditFilmRatingText(credit) }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                  <div v-else-if="currentViewMode === '1' && selectedMedium === 'tv'" class="container g-0 credits-list person-tv-credits-list">
                    <p
                      v-if="visibleCredits.length === 0"
                      class="person-no-results"
                      data-testid="person-no-credits"
                    >
                      {{ noCreditsMessage }}
                    </p>
                    <div
                      v-for="(credit, index) in visibleCredits"
                      :key="`tv-${credit.title}`"
                      class="person-tv-credit-group"
                      data-testid="person-tv-credit-group"
                    >
                      <div class="row g-0 single-credit-row person-tv-credit-row" data-testid="person-tv-credit-row">
                        <div class="col-3 g-0 film-poster-col">
                          <div class="film-poster-container">
                            <a :href="credit.film_url || '#'"><img class="film-poster" :src="creditPosterUrl(credit)" :alt="`${credit.title} poster`" loading="lazy" decoding="async"></a>
                          </div>
                        </div>
                        <div class="col g-0">
                          <div class="container g-0 single-credit-container">
                            <div class="row g-0 credit-title-row">
                              <div class="col g-0">
                                <a :href="credit.film_url || '#'">
                                  <h3 class="credit-film-title">
                                    {{ credit.title }}
                                    <span v-if="credit.year" class="release-year">({{ credit.year }})</span>
                                  </h3>
                                </a>
                              </div>
                            </div>
                            <div v-if="credit.overview" class="row g-0 overview-row">
                              <div class="col g-0">
                                <p class="credit-film-overview">
                                  <span data-testid="person-credit-overview-body">{{ displayedCreditOverview(credit) }}</span>
                                  <button
                                    v-if="hasExpandableCreditOverview(credit)"
                                    class="overview-toggle"
                                    data-testid="person-credit-overview-toggle"
                                    type="button"
                                    @click="toggleCreditOverview(credit)"
                                  >
                                    <span class="overview-toggle__icon" aria-hidden="true">{{ isCreditOverviewExpanded(credit) ? '▲' : '▼' }}</span>
                                  </button>
                                </p>
                              </div>
                            </div>
                            <div class="row g-0">
                              <div class="col g-0">
                                <div class="container g-0 single-show-specs">
                                  <div v-if="credit.character_name" class="row g-0 character-name-row">
                                    <div class="col g-0 character-parts">
                                      <div class="person-tv-character-line">
                                        <span class="person-tv-character-label">as</span>
                                        <span class="character-name">{{ credit.character_name }}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div v-if="credit.main_cast?.length" class="row g-0 stars-row">
                                    <div class="col g-0">
                                      <div class="stars person-tv-main-cast">
                                        <span class="credit-film-runtime">Main cast:</span>
                                        <span
                                          v-for="castMember in credit.main_cast"
                                          :key="`${credit.title}-${castMember.name}`"
                                          class="person-tv-cast-item"
                                        >
                                          <a :href="castMember.url || '#'">{{ castMember.name }}</a>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="row g-0 person-show-credits-button-row">
                              <div class="col g-0">
                                <div class="person-show-buttons person-show-buttons--without-calendar">
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn left-btn"
                                      :class="{ 'btn--watchlisted': isCreditWatchlisted(credit) }"
                                      :data-show-slug="creditShowSlug(credit) || ''"
                                      data-testid="person-credit-watchlist-action"
                                      type="button"
                                      :disabled="isCreditWatchlistPending(credit)"
                                      @click="toggleCreditWatchlist(credit)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>&nbsp;<span class="watchlist-long">{{ creditWatchlistButtonText(credit) }}</span>
                                        <span class="watchlist-short">{{ creditWatchlistButtonShortText(credit) }}</span>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn left-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>&nbsp;<span class="watchlist-long">Add to watchlist</span>
                                        <span class="watchlist-short">Watchlist</span>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn"
                                      :class="[
                                        'right-btn',
                                        { 'btn--favourited': isCreditFavourited(credit) },
                                      ]"
                                      :data-show-slug="creditShowSlug(credit) || ''"
                                      data-testid="person-credit-favourite-action"
                                      type="button"
                                      :disabled="isCreditFavouritePending(credit)"
                                      @click="toggleCreditFavourite(credit)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>&nbsp;<span class="fav-long">{{ creditFavouriteButtonText(credit) }}</span>
                                        <span class="fav-short">{{ creditFavouriteButtonShortText(credit) }}</span>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn right-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>&nbsp;<span class="fav-long">Add to favourites</span>
                                        <span class="fav-short">Favourite</span>
                                      </div>
                                    </a>
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row g-0 person-show-credits-button-row-sm">
                        <div class="col g-0">
                          <div class="person-show-buttons person-show-buttons--without-calendar">
                            <template v-if="isAuthenticated">
                              <button
                                class="fs-btn left-btn"
                                :class="{ 'btn--watchlisted': isCreditWatchlisted(credit) }"
                                :data-show-slug="creditShowSlug(credit) || ''"
                                data-testid="person-credit-watchlist-action"
                                type="button"
                                :disabled="isCreditWatchlistPending(credit)"
                                @click="toggleCreditWatchlist(credit)"
                              >
                                <div class="default-btn">
                                  <i class="ion-bookmark"></i>&nbsp;<span class="watchlist-long">{{ creditWatchlistButtonText(credit) }}</span>
                                  <span class="watchlist-short">{{ creditWatchlistButtonShortText(credit) }}</span>
                                </div>
                              </button>
                            </template>
                            <template v-else>
                              <a class="fs-btn left-btn" :href="loginPath">
                                <div class="default-btn">
                                  <i class="ion-bookmark"></i>&nbsp;<span class="watchlist-long">Add to watchlist</span>
                                  <span class="watchlist-short">Watchlist</span>
                                </div>
                              </a>
                            </template>
                            <template v-if="isAuthenticated">
                              <button
                                class="fs-btn"
                                :class="[
                                  'right-btn',
                                  { 'btn--favourited': isCreditFavourited(credit) },
                                ]"
                                :data-show-slug="creditShowSlug(credit) || ''"
                                data-testid="person-credit-favourite-action"
                                type="button"
                                :disabled="isCreditFavouritePending(credit)"
                                @click="toggleCreditFavourite(credit)"
                              >
                                <div class="default-btn">
                                  <i class="ion-heart"></i>&nbsp;<span class="fav-long">{{ creditFavouriteButtonText(credit) }}</span>
                                  <span class="fav-short">{{ creditFavouriteButtonShortText(credit) }}</span>
                                </div>
                              </button>
                            </template>
                            <template v-else>
                              <a class="fs-btn right-btn" :href="loginPath">
                                <div class="default-btn">
                                  <i class="ion-heart"></i>&nbsp;<span class="fav-long">Add to favourites</span>
                                  <span class="fav-short">Favourite</span>
                                </div>
                              </a>
                            </template>
                          </div>
                        </div>
                      </div>
                      <div v-if="credit.overview" class="row g-0 overview-row-sm">
                        <div class="col g-0">
                          <p class="credit-film-overview">
                            <span data-testid="person-credit-overview-body">{{ displayedCreditOverview(credit) }}</span>
                            <button
                              v-if="hasExpandableCreditOverview(credit)"
                              class="overview-toggle"
                              data-testid="person-credit-overview-toggle"
                              type="button"
                              @click="toggleCreditOverview(credit)"
                            >
                              <span class="overview-toggle__icon" aria-hidden="true">{{ isCreditOverviewExpanded(credit) ? '▲' : '▼' }}</span>
                            </button>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="currentViewMode === '2'" class="person-posters-grid" data-testid="person-posters-grid">
                    <p
                      v-if="visibleCredits.length === 0"
                      class="person-no-results"
                      data-testid="person-no-credits"
                    >
                      {{ noCreditsMessage }}
                    </p>
                    <div
                      v-for="credit in visibleCredits"
                      :key="`poster-${credit.title}`"
                      class="person-poster-card"
                      data-testid="person-poster-grid-item"
                    >
                      <a class="person-poster-link" :href="credit.film_url || '#'">
                        <img
                          class="person-poster-grid-image"
                          :src="creditPosterUrl(credit)"
                          :alt="`${credit.title} poster`"
                          loading="lazy"
                          decoding="async"
                        >
                        <div
                          v-if="creditUsesPlaceholderPoster(credit)"
                          class="person-poster-placeholder-overlay"
                          data-testid="person-poster-placeholder-overlay"
                        >
                          <h3 class="person-poster-placeholder-title">{{ credit.title }}</h3>
                          <p v-if="credit.year" class="person-poster-placeholder-year">{{ credit.year }}</p>
                        </div>
                        <div
                          v-if="selectedActivity === 'rated' && creditRatingValue(credit)"
                          class="person-rating-poster-overlay"
                          data-testid="person-rating-poster-overlay"
                        >
                          <i class="person-rating-poster-overlay__star ion-ios-star" aria-hidden="true"></i>
                          <span class="person-rating-poster-overlay__value">
                            {{ creditRatingValue(credit) }}
                          </span>
                        </div>
                      </a>
                    </div>
                  </div>
                  <div
                    v-else-if="currentViewMode === '3'"
                    class="container g-0 person-compact-credits-list"
                    data-testid="person-compact-credits-list"
                  >
                    <p
                      v-if="visibleCredits.length === 0"
                      class="person-no-results"
                      data-testid="person-no-credits"
                    >
                      {{ noCreditsMessage }}
                    </p>
                    <div
                      v-for="(credit, index) in visibleCredits"
                      :key="`compact-${credit.title}`"
                      :class="[
                        'row',
                        'g-0',
                        'person-compact-credit-row',
                        index === visibleCredits.length - 1 ? 'person-compact-credit-row--last' : null,
                      ]"
                      data-testid="person-compact-credit-row"
                    >
                      <div class="col g-0">
                        <div class="container g-0 person-compact-credit-container">
                          <div class="row g-0 credit-title-row person-compact-credit-title-row">
                            <div class="col g-0">
                              <a :href="credit.film_url || '#'">
                                <h3 class="credit-film-title person-compact-credit-film-title">
                                  {{ credit.title }}
                                  <span v-if="credit.year" class="release-year">({{ credit.year }})</span>
                                </h3>
                              </a>
                            </div>
                          </div>
                          <div class="row g-0 person-compact-credit-actions-row">
                            <div class="col g-0">
                              <div
                                :class="[
                                  'person-buttons',
                                  'person-buttons--credit',
                                  'person-buttons--compact',
                                  selectedMedium === 'tv' ? 'person-buttons--without-calendar' : null,
                                ]"
                                data-testid="person-credit-action-group"
                              >
                                <template v-if="selectedMedium === 'film'">
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn left-btn"
                                      :class="{ 'btn--watched': isCreditWatched(credit) }"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-watch-action"
                                      type="button"
                                      :disabled="isCreditWatchPending(credit)"
                                      @click="markCreditWatched(credit)"
                                    >
                                      <div class="default-btn">
                                        <i :class="creditWatchIconClass(credit)"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="fs-btn left-btn"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-watch-action"
                                      :href="loginPath"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-plus"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn middle-btn"
                                      :class="{ 'btn--watchlisted': isCreditWatchlisted(credit) }"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-watchlist-action"
                                      type="button"
                                      :disabled="isCreditWatchlistPending(credit)"
                                      @click="toggleCreditWatchlist(credit)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="fs-btn middle-btn"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-watchlist-action"
                                      :href="loginPath"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn right-btn"
                                      :class="{ 'btn--favourited': isCreditFavourited(credit) }"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-favourite-action"
                                      type="button"
                                      :disabled="isCreditFavouritePending(credit)"
                                      @click="toggleCreditFavourite(credit)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="fs-btn right-btn"
                                      :data-film-slug="creditFilmSlug(credit) || ''"
                                      data-testid="person-credit-favourite-action"
                                      :href="loginPath"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </a>
                                  </template>
                                </template>
                                <template v-else>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn left-btn"
                                      :class="{ 'btn--watchlisted': isCreditWatchlisted(credit) }"
                                      :data-show-slug="creditShowSlug(credit) || ''"
                                      data-testid="person-credit-watchlist-action"
                                      type="button"
                                      :disabled="isCreditWatchlistPending(credit)"
                                      @click="toggleCreditWatchlist(credit)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="fs-btn left-btn"
                                      :data-show-slug="creditShowSlug(credit) || ''"
                                      data-testid="person-credit-watchlist-action"
                                      :href="loginPath"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      :class="[
                                        'fs-btn',
                                        'right-btn',
                                        { 'btn--favourited': isCreditFavourited(credit) },
                                      ]"
                                      :data-show-slug="creditShowSlug(credit) || ''"
                                      data-testid="person-credit-favourite-action"
                                      type="button"
                                      :disabled="isCreditFavouritePending(credit)"
                                      @click="toggleCreditFavourite(credit)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      class="fs-btn right-btn"
                                      :data-show-slug="creditShowSlug(credit) || ''"
                                      data-testid="person-credit-favourite-action"
                                      :href="loginPath"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </a>
                                  </template>
                                </template>
                              </div>
                            </div>
                            <div class="col g-0">
                              <div
                                class="rating-stars person-compact-rating-stars person-compact-rating-stars--full"
                                @mouseleave="resetCreditRatingPreview(credit)"
                              >
                                <div class="person-compact-rating-stars-list" data-testid="person-compact-full-stars">
                                  <template v-if="isAuthenticated">
                                    <button
                                      v-for="starNumber in 10"
                                      :key="`${credit.title}-compact-star-${starNumber}`"
                                      class="person-compact-rating-star-button"
                                      data-testid="person-compact-rating-star"
                                      type="button"
                                      :disabled="isCreditRatingPending(credit)"
                                      @mouseenter="previewCreditRating(credit, starNumber)"
                                      @click="setCreditRating(credit, starNumber)"
                                    >
                                      <i :class="creditRatingStarClass(credit, starNumber)"></i>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a
                                      v-for="starNumber in 10"
                                      :key="`${credit.title}-compact-star-${starNumber}`"
                                      data-testid="person-compact-rating-star"
                                      :data-film-slug="selectedMedium === 'film' ? creditFilmSlug(credit) || '' : null"
                                      :data-show-slug="selectedMedium === 'tv' ? creditShowSlug(credit) || '' : null"
                                      :href="loginPath"
                                    >
                                      <i class="rating-star ion-ios-star-outline"></i>
                                    </a>
                                  </template>
                                </div>
                                <div class="current-credits-rating person-compact-current-rating">
                                  <p>{{ creditRatingText(credit) }}</p>
                                </div>
                              </div>
                              <div class="rating-stars person-compact-rating-stars person-compact-rating-stars--single">
                                <template v-if="isAuthenticated">
                                  <button
                                    class="person-credit-rating-summary"
                                    :data-film-slug="selectedMedium === 'film' ? creditFilmSlug(credit) || '' : null"
                                    :data-show-slug="selectedMedium === 'tv' ? creditShowSlug(credit) || '' : null"
                                    data-testid="person-compact-rating-summary-star"
                                    type="button"
                                    :disabled="isCreditRatingPending(credit)"
                                    @click="openCompactCreditFilmRatingPicker(credit)"
                                  >
                                    <i :class="creditSummaryRatingStarClass(credit)"></i>
                                  </button>
                                </template>
                                <template v-else>
                                  <a
                                    class="person-credit-rating-summary"
                                    :data-film-slug="selectedMedium === 'film' ? creditFilmSlug(credit) || '' : null"
                                    :data-show-slug="selectedMedium === 'tv' ? creditShowSlug(credit) || '' : null"
                                    data-testid="person-compact-rating-summary-star"
                                    :href="loginPath"
                                  >
                                    <i class="rating-star ion-ios-star-outline"></i>
                                  </a>
                                </template>
                                <div class="current-credits-rating person-compact-current-rating">
                                  <p>{{ creditRatingText(credit) }}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="currentViewMode === '4'"
                    class="container g-0 person-text-only-credits-list"
                    data-testid="person-text-only-credits-list"
                  >
                    <p
                      v-if="visibleCredits.length === 0"
                      class="person-no-results"
                      data-testid="person-no-credits"
                    >
                      {{ noCreditsMessage }}
                    </p>
                    <div
                      v-for="(credit, index) in visibleCredits"
                      :key="`text-only-${credit.title}`"
                      :class="[
                        'row',
                        'g-0',
                        'person-text-only-credit-row',
                        index === visibleCredits.length - 1 ? 'person-text-only-credit-row--last' : null,
                      ]"
                      data-testid="person-text-only-credit-row"
                    >
                      <div class="col g-0">
                        <div class="container g-0 person-text-only-credit-container">
                          <div class="row g-0 credit-title-row">
                            <div class="col g-0">
                              <div class="person-text-only-row">
                                <a :href="credit.film_url || '#'">
                                  <h3 class="credit-film-title person-text-only-credit-film-title">
                                    {{ credit.title }}
                                  </h3>
                                </a>
                                <span v-if="credit.year" class="release-year person-text-only-release-year">
                                  <span class="person-text-only-parenthesis">(</span>{{ credit.year }}<span class="person-text-only-parenthesis">)</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-3 g-0 filters-section" data-testid="person-filters-sidebar">
                  <div class="container person-filters">
                    <div class="row g-0 filters-title-row">
                      <div class="col g-0">
                        <h3>Filters:</h3>
                      </div>
                    </div>

                    <template v-for="group in filterGroups" :key="`sidebar-${group.title}`">
                      <div class="row g-0 single-filter-title-row">
                        <div class="col g-0">
                          <h3>{{ group.title }}</h3>
                        </div>
                      </div>
                      <div class="row g-0 filter-row">
                        <div class="col g-0">
                          <a
                            v-for="option in group.options"
                            :key="option.label"
                            :class="{ active: option.active }"
                            href="#"
                            @click.prevent="applyFilter(group.key, option.value)"
                          >
                            {{ option.label }}
                          </a>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>
    <div v-else-if="activeSection === 'lists'" class="container g-0" data-testid="lists-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0 film-lists-panel">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="person-lists-heading">{{ personListsHeading }}</h2>
              </div>
            </div>

            <div class="row g-0 list-addition-row">
              <div class="col g-0">
                <template v-if="isAuthenticated">
                  <button
                    class="btn-sm list-addition-btn"
                    data-testid="person-list-open"
                    type="button"
                    @click="openPersonListModal"
                  >
                    + Add to list
                  </button>
                </template>
                <template v-else>
                  <a class="btn-sm list-addition-btn" data-testid="person-list-open" :href="loginPath">+ Add to list</a>
                </template>
              </div>
            </div>

            <div class="row g-0 credits-filter-container">
              <div class="col g-0">
                <p class="list-filter-title">Filter lists by title or creator</p>
                <input
                  id="person-lists-filter"
                  v-model="personListsFilter"
                  class="credits-filter"
                  data-testid="person-lists-filter"
                  type="search"
                  placeholder="Filter by title or creator..."
                >
              </div>
            </div>

            <p v-if="personListsPageError" class="film-lists-empty" data-testid="person-lists-error">
              {{ personListsPageError }}
            </p>
            <p v-else-if="personListsPageLoading" class="film-lists-empty" data-testid="person-lists-loading">
              Loading lists...
            </p>
            <p
              v-else-if="personListsPageLoaded && filteredPersonLists.length === 0 && personLists.length === 0"
              class="film-lists-empty"
              data-testid="person-lists-empty"
            >
              This person doesn&apos;t appear on any lists, yet.
            </p>
            <p
              v-else-if="personListsPageLoaded && filteredPersonLists.length === 0"
              class="film-lists-empty"
              data-testid="person-lists-no-results"
            >
              No lists match that filter.
            </p>

            <div
              v-else-if="filteredPersonLists.length > 0"
              class="container g-0"
            >
              <div
                v-for="(personList, index) in filteredPersonLists"
                :key="personList.id"
                :class="[
                  'row',
                  'g-0',
                  'lists-row',
                  index === 0 ? 'first-list-row' : null,
                ]"
                data-testid="person-list-page-entry"
              >
                <div class="col g-0">
                  <div class="list-posters">
                    <div
                      v-for="item in personList.items"
                      :key="`${personList.id}-${item.id}`"
                      class="list-poster"
                    >
                      <a :href="personListDetailPath(personList)">
                        <img :src="item.poster_url" :alt="`${item.title} portrait`" loading="lazy" decoding="async">
                      </a>
                    </div>
                  </div>
                  <a class="list-title" :href="personListDetailPath(personList)">{{ personList.title }}</a>
                  <CuratedListLabel v-if="personList.is_curated" />
                  <p v-else-if="personList.official" class="list-owner official-list">Official list</p>
                  <p v-else class="list-owner">by <a :href="personList.ownerHref">{{ personList.owner_name }}</a></p>
                </div>
              </div>
              <nav
                v-if="showPersonListsPagination"
                class="person-lists-pagination person-lists-pagination--with-mobile"
                aria-label="Person lists pagination"
              >
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: !canGoToPreviousPersonListsPage || personListsPageLoading }">
                    <button
                      class="page-link"
                      data-testid="person-lists-prev"
                      type="button"
                      @click="goToPersonListsPage(personListsPageNumber - 1)"
                    >
                      Previous
                    </button>
                  </li>
                  <li
                    v-for="pageItem in personListsPages"
                    :key="`person-lists-${pageItem.key}`"
                    class="page-item"
                    :class="{ active: pageItem.page === personListsPageNumber, disabled: pageItem.ellipsis }"
                  >
                    <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                      {{ pageItem.label }}
                    </span>
                    <button
                      v-else
                      class="page-link"
                      :data-testid="`person-lists-page-${pageItem.page}`"
                      type="button"
                      :aria-label="`Go to page ${pageItem.page}`"
                      @click="goToPersonListsPage(pageItem.page)"
                    >
                      {{ pageItem.label }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: !canGoToNextPersonListsPage || personListsPageLoading }">
                    <button
                      class="page-link"
                      data-testid="person-lists-next"
                      type="button"
                      @click="goToPersonListsPage(personListsPageNumber + 1)"
                    >
                      Next
                    </button>
                  </li>
                </ul>
                <CompactMobilePagination
                  :current-page="personListsPageNumber"
                  :total-pages="personListsNumPages"
                  :loading="personListsPageLoading"
                  test-id-prefix="person-lists"
                  @page-change="goToPersonListsPage"
                />
              </nav>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>
    <div v-else class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <CommentsPanel
            :base-path="basePath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            :target-id="personId ? Number(personId) : null"
            target-type="person"
            test-id-prefix="person"
          />
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>
      </div>
    </Transition>
    <Teleport to="body">
      <div
        v-if="personListModalOpen"
        class="film-watch-modal-overlay"
        data-testid="person-list-modal-overlay"
      >
        <button
          class="film-watch-modal-backdrop"
          type="button"
          aria-label="Close list modal"
          :disabled="personListModalPending"
          @click="closePersonListModal"
        ></button>
        <div
          class="film-watch-modal film-list-modal"
          data-testid="person-list-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="person-list-modal-title"
        >
          <div class="film-watch-modal__header">
            <h2 id="person-list-modal-title" class="film-watch-modal__title film-list-modal__title">
              Add "{{ person.name }}"<br>to list:
            </h2>
            <button
              class="film-watch-modal__close"
              data-testid="person-list-close"
              type="button"
              aria-label="Close list modal"
              :disabled="personListModalPending"
              @click="closePersonListModal"
            >
              <svg class="film-watch-modal__close-icon" viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.4" />
                <path
                  d="M7 7 L13 13 M13 7 L7 13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <p
            v-if="personListModalError"
            class="film-watch-modal__error"
            data-testid="person-list-error"
          >
            {{ personListModalError }}
          </p>

          <form
            v-if="!personListCreateMode"
            class="film-watch-modal__form"
            @submit.prevent="submitPersonListSelection"
          >
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__field">
                <span>List</span>
                <div class="film-list-picker" @click.stop @mousedown.stop>
                  <button
                    class="film-list-picker__toggle"
                    :class="{ 'film-list-picker__toggle--open': personListPickerOpen }"
                    data-testid="person-list-select"
                    type="button"
                    :disabled="personListModalPending || personListModalLoading"
                    @click.stop="personListPickerOpen = !personListPickerOpen"
                  >
                    <span class="film-list-picker__label">{{ selectedPersonListLabel }}</span>
                    <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                  </button>
                  <div
                    v-if="personListPickerOpen"
                    class="film-list-picker__menu"
                    data-testid="person-list-picker-menu"
                  >
                    <button
                      class="film-list-picker__option"
                      :class="{ 'film-list-picker__option--selected': !personListSelectedListId }"
                      data-testid="person-list-option-empty"
                      type="button"
                      @click.stop="selectPersonList('')"
                    >
                      Choose list...
                    </button>
                    <button
                      v-for="listOption in personListOptions"
                      :key="listOption.id"
                      class="film-list-picker__option"
                      :class="{ 'film-list-picker__option--selected': String(listOption.id) === personListSelectedListId }"
                      :data-testid="`person-list-option-${listOption.id}`"
                      type="button"
                      @click.stop="selectPersonList(String(listOption.id))"
                    >
                      {{ listOption.title }}
                    </button>
                  </div>
                </div>
                <select
                  v-model="personListSelectedListId"
                  :disabled="personListModalPending || personListModalLoading"
                  class="film-list-picker__native"
                  tabindex="-1"
                  aria-hidden="true"
                >
                  <option value="">Choose list...</option>
                  <option
                    v-for="listOption in personListOptions"
                    :key="listOption.id"
                    :value="String(listOption.id)"
                  >
                    {{ listOption.title }}
                  </option>
                </select>
              </div>
            </section>

            <div class="film-watch-modal__actions film-list-modal__actions">
              <button
                class="film-watch-modal__action film-watch-modal__action--secondary"
                data-testid="person-list-create-toggle"
                type="button"
                :disabled="personListModalPending"
                @click="openPersonListCreateMode"
              >
                Create new list
              </button>
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  data-testid="person-list-cancel"
                  type="button"
                  :disabled="personListModalPending"
                  @click="closePersonListModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="person-list-submit"
                  type="submit"
                  :disabled="personListModalPending || personListModalLoading || !personListSelectedListId"
                >
                  Add to list
                </button>
              </div>
            </div>
          </form>

          <form
            v-else
            class="film-watch-modal__form"
            @submit.prevent="createPersonList"
          >
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Create a new list</h3>
              </div>
              <label class="film-watch-modal__field">
                <span>Title</span>
                <input
                  v-model="personListCreateTitle"
                  class="film-list-modal__input"
                  data-testid="person-list-create-title"
                  type="text"
                  maxlength="255"
                  :disabled="personListModalPending"
                >
              </label>
              <label class="film-watch-modal__field">
                <span>Description</span>
                <textarea
                  v-model="personListCreateDescription"
                  class="film-watch-modal__textarea"
                  data-testid="person-list-create-description"
                  maxlength="1000"
                  rows="5"
                  :disabled="personListModalPending"
                ></textarea>
              </label>
              <label class="film-watch-modal__checkbox">
                <input
                  v-model="personListCreatePublic"
                  data-testid="person-list-create-public"
                  type="checkbox"
                  :disabled="personListModalPending"
                >
                <span>Public</span>
              </label>
            </section>

            <div class="film-watch-modal__actions">
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  data-testid="person-list-create-cancel"
                  type="button"
                  :disabled="personListModalPending"
                  @click="closePersonListCreateMode"
                >
                  Back
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="person-list-create-submit"
                  type="submit"
                  :disabled="personListModalPending"
                >
                  Create
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div
        v-if="compactCreditFilmRatingPickerCredit"
        class="person-compact-rating-picker-overlay"
        data-testid="person-compact-rating-picker-overlay"
        @click.self="closeCompactCreditFilmRatingPicker"
      >
        <div class="person-compact-rating-picker" data-testid="person-compact-rating-picker">
          <button
            class="person-compact-rating-picker__close"
            data-testid="person-compact-rating-picker-close"
            type="button"
            aria-label="Close rating picker"
            @click="closeCompactCreditFilmRatingPicker"
          >
            <svg
              class="person-compact-rating-picker__close-icon"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.4" />
              <path
                d="M7 7 L13 13 M13 7 L7 13"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <p class="person-compact-rating-picker__title">
            {{ compactCreditFilmRatingPickerCredit.title }}
          </p>
          <div class="person-compact-rating-picker__stars">
            <button
              v-for="star in 10"
              :key="`person-compact-rating-${creditRatingSlug(compactCreditFilmRatingPickerCredit)}-${star}`"
              class="person-compact-rating-picker__star"
              data-testid="person-compact-rating-picker-star"
              type="button"
              :disabled="isCreditRatingPending(compactCreditFilmRatingPickerCredit)"
              @mouseenter="previewCreditRating(compactCreditFilmRatingPickerCredit, star)"
              @click="selectCompactCreditFilmRating(star)"
            >
              <i :class="creditRatingStarClass(compactCreditFilmRatingPickerCredit, star)"></i>
            </button>
          </div>
          <button
            v-if="creditRatingValue(compactCreditFilmRatingPickerCredit)"
            class="person-compact-rating-picker__reset"
            data-testid="person-compact-rating-picker-reset"
            type="button"
            :disabled="isCreditRatingPending(compactCreditFilmRatingPickerCredit)"
            @click="removeCompactCreditFilmRating"
          >
            Reset rating
          </button>
        </div>
      </div>
    </Teleport>
    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import CuratedListLabel from '../components/CuratedListLabel.vue'
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CommentsPanel from '../components/CommentsPanel.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import NotFoundView from './NotFoundView.vue'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-person/person-detail.css'
import '../assets/single-person/person-show.css'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { compactPaginationItems } from '../utils/pagination'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'
import { clipOverviewWithSuffix, hasExpandableOverview } from '../utils/overview'

const route = useRoute()
const capturePageView = usePageViewTracking()
const router = useRouter()
const auth = useAuthStore()
auth.initFromStorage()
const personSlug = computed(() => String(route.params.slug || ''))
const basePath = computed(() => `/people/${personSlug.value}`)
const loginPath = computed(() => buildLoginPath(route.fullPath || basePath.value))
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const authUsername = computed(() => auth.state.username || 'Account')
const personSection = computed(() => String(route.params.section || '').toLowerCase())
const activeSection = computed(() => (
  ['comments', 'discussion'].includes(personSection.value)
    ? 'comments'
    : (personSection.value === 'lists' ? 'lists' : 'filmography')
))
const personId = computed(() => {
  const match = personSlug.value.match(/^(\d+)-/)
  return match ? match[1] : null
})
const personDatabaseId = ref(null)
const assets = {
  logo: '/static/spa/branding/logo.png',
}

const defaultHeroAssets = {
  poster: '/static/films/img/placeholder-person.svg',
  background: '/media/films/backgrounds/default-bg.jpg',
}
const initialHeroAssets = {
  poster: '',
  background: defaultHeroAssets.background,
}
const filmPosterPlaceholder = '/static/films/img/placeholder-film-camera.svg'
const personViewModeStorageKey = 'filmsomniac-person-view-mode'
const supportedDepartmentSections = ['directing', 'writing', 'crew']

function routeSectionToMedium(section) {
  if (section === 'tv') {
    return 'tv'
  }
  if (section === 'lists') {
    return 'lists'
  }
  return 'film'
}

function defaultTypeFallbackForMedium(medium) {
  return medium === 'tv' ? 'tv_show' : 'feature'
}

function defaultReleaseStatusFallbackForMedium(medium) {
  return medium === 'tv' ? 'ongoing' : 'released'
}

const heroAssets = ref({ ...initialHeroAssets })
const personPortraitStatus = ref('loading')
const personHeroBackgroundLoaded = ref(false)
const mobileSearchOpen = ref(false)

const personHeroBackgroundClasses = computed(() => [
  'person-hero-background',
  personHeroBackgroundLoaded.value
    ? 'person-hero-background--loaded'
    : 'person-hero-background--loading',
])
const personPortraitLoading = computed(() => personPortraitStatus.value === 'loading')
const personPortraitImageSrc = computed(() => heroAssets.value.poster || '')
const personPortraitFrameClasses = computed(() => [
  'image-load-frame',
  'image-load-frame--portrait',
  personPortraitStatus.value === 'loading' ? 'image-load-frame--loading' : null,
  personPortraitStatus.value === 'loaded' ? 'image-load-frame--loaded' : null,
  personPortraitStatus.value === 'fallback' ? 'image-load-frame--fallback' : null,
])
const personPortraitImageClasses = computed(() => [
  'hero__poster',
  'image-load-frame__image',
  personPortraitStatus.value !== 'loading' ? 'image-load-frame__image--visible' : null,
])

function resetPersonPortraitLoading(src = heroAssets.value.poster) {
  if (!src) {
    personPortraitStatus.value = 'loading'
    return
  }
  personPortraitStatus.value = src === defaultHeroAssets.poster ? 'fallback' : 'loading'
}

function handlePersonPortraitLoad() {
  personPortraitStatus.value = heroAssets.value.poster === defaultHeroAssets.poster
    ? 'fallback'
    : 'loaded'
}

function handlePersonPortraitError() {
  if (heroAssets.value.poster !== defaultHeroAssets.poster) {
    heroAssets.value = {
      ...heroAssets.value,
      poster: defaultHeroAssets.poster,
    }
    return
  }
  personPortraitStatus.value = 'fallback'
}

function handlePersonHeroBackgroundLoad() {
  personHeroBackgroundLoaded.value = true
}

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function handlePersonDetailKeydown(event) {
  if (event.key === 'Escape') {
    closePersonListModal()
    closeCompactCreditFilmRatingPicker()
  }
}

const person = ref({
  name: '',
  jobs: '',
  bio: '',
})
const documentTitle = computed(() => buildDocumentTitle([person.value.name], personSection.value))

const relatedResources = ref([])
const personLists = ref([])
const personListsCount = ref(0)
const personListsPageLoading = ref(false)
const personListsPageLoaded = ref(false)
const personListsPageError = ref('')
const personListsPageNumber = ref(1)
const personListsNumPages = ref(1)
const personListsFilter = ref('')
const personFavourited = ref(false)
const personFavouritePending = ref(false)
const personFavouriteLoaded = ref(false)
const filmographyByDepartment = ref({
  acting: [],
  directing: [],
  writing: [],
  crew: [],
})
const showographyByDepartment = ref({
  acting: [],
})
const filmDepartmentCounts = ref({
  acting: 0,
  directing: 0,
  writing: 0,
  crew: 0,
})
const showDepartmentCounts = ref({
  acting: 0,
})
const hasFilmCredits = ref(false)
const hasShowCredits = ref(false)
const selectedMedium = ref(routeSectionToMedium(personSection.value))
const selectedDepartment = ref('acting')
const selectedType = ref(defaultTypeFallbackForMedium(selectedMedium.value))
const selectedReleaseStatus = ref(defaultReleaseStatusFallbackForMedium(selectedMedium.value))
const selectedActivity = ref('all')
const hasLoadedPersonSummary = ref(false)
const hasLoadedPersonPayload = ref(false)
const loadedCreditMedium = ref(null)
const loadedCreditKeys = ref({})
const fullyLoadedCreditKeys = ref({})
const personNotFound = ref(false)
const filmWatchlistStates = ref({})
const filmWatchlistPendingBySlug = ref({})
const filmWatchlistLoadedBySlug = ref({})
const filmWatchedStates = ref({})
const filmWatchedPendingBySlug = ref({})
const filmWatchedLoadedBySlug = ref({})
const filmFavouriteStates = ref({})
const filmFavouritePendingBySlug = ref({})
const filmFavouriteLoadedBySlug = ref({})
const filmRatingStates = ref({})
const filmRatingPendingBySlug = ref({})
const filmRatingLoadedBySlug = ref({})
const filmRatingHoverBySlug = ref({})
const showRatingStates = ref({})
const showRatingPendingBySlug = ref({})
const showRatingLoadedBySlug = ref({})
const showRatingHoverBySlug = ref({})
const showWatchlistStates = ref({})
const showWatchlistPendingBySlug = ref({})
const showWatchlistLoadedBySlug = ref({})
const showFavouriteStates = ref({})
const showFavouritePendingBySlug = ref({})
const showFavouriteLoadedBySlug = ref({})
const showCalendarStates = ref({})
const showCalendarPendingBySlug = ref({})
const showCalendarLoadedBySlug = ref({})
const showCalendarEnabledBySlug = ref({})
const compactCreditFilmRatingPickerCredit = ref(null)
const personListModalOpen = ref(false)
const personListModalPending = ref(false)
const personListModalLoading = ref(false)
const personListModalError = ref('')
const personListCreateMode = ref(false)
const personListOptions = ref([])
const personListSelectedListId = ref('')
const personListPickerOpen = ref(false)
const personListCreateTitle = ref('')
const personListCreateDescription = ref('')
const personListCreatePublic = ref(true)

const sortMode = ref('newest')
const currentViewMode = ref(getStoredPersonViewMode())
const expandedCreditOverviewIds = ref({})
const isPersonFavourited = computed(() => personFavourited.value)
const personFavouriteButtonText = computed(() => (
  isPersonFavourited.value ? 'Favourited' : 'Add to favourites'
))
const personFavouriteButtonShortText = computed(() => (
  isPersonFavourited.value ? 'Favourited' : 'Favourite'
))

const sortButtons = computed(() => [
  { key: 'title-asc', label: 'Title A-Z', first: true, active: sortMode.value === 'title-asc' },
  { key: 'title-desc', label: 'Title Z-A', active: sortMode.value === 'title-desc' },
  { key: 'oldest', label: 'Oldest', active: sortMode.value === 'oldest' },
  { key: 'newest', label: 'Newest', active: sortMode.value === 'newest' },
])

const viewModeButtons = computed(() => ([
  {
    key: '1',
    label: 'Detailed view',
    first: true,
    active: currentViewMode.value === '1',
    enabled: true,
  },
  {
    key: '2',
    label: 'Poster grid view',
    active: currentViewMode.value === '2',
    enabled: true,
  },
  {
    key: '3',
    label: 'Compact list view',
    active: currentViewMode.value === '3',
    enabled: true,
  },
  {
    key: '4',
    label: 'Text list view',
    active: currentViewMode.value === '4',
    enabled: true,
  },
]))

const hasFilmMedium = computed(() => (
  hasFilmCredits.value
    || Object.values(filmographyByDepartment.value).some((entries) => Array.isArray(entries) && entries.length > 0)
))

const hasShowMedium = computed(() => (
  hasShowCredits.value
    || Object.values(showographyByDepartment.value).some((entries) => Array.isArray(entries) && entries.length > 0)
))

const departmentCounts = computed(() => {
  if (selectedMedium.value === 'tv') {
    return {
      acting: showDepartmentCounts.value.acting || showographyByDepartment.value.acting.length,
    }
  }

  return {
    acting: filmDepartmentCounts.value.acting || filmographyByDepartment.value.acting.length,
    directing: filmDepartmentCounts.value.directing || filmographyByDepartment.value.directing.length,
    writing: filmDepartmentCounts.value.writing || filmographyByDepartment.value.writing.length,
    crew: filmDepartmentCounts.value.crew || filmographyByDepartment.value.crew.length,
  }
})

const departmentCredits = computed(() => {
  if (selectedMedium.value === 'tv') {
    return showographyByDepartment.value[selectedDepartment.value] || []
  }
  return filmographyByDepartment.value[selectedDepartment.value] || []
})

const firstAvailableDepartment = computed(() => {
  if (selectedMedium.value === 'tv') {
    return 'acting'
  }

  return ['acting', 'directing', 'writing', 'crew'].find(
    (department) => departmentCounts.value[department] > 0,
  ) || 'acting'
})

function countMatches(entries, key, expectedValue) {
  return entries.reduce((total, entry) => (
    entry[key] === expectedValue ? total + 1 : total
  ), 0)
}

const currentTypeCounts = computed(() => {
  if (selectedMedium.value === 'tv') {
    return {
      all: departmentCredits.value.length,
      tv_show: countMatches(departmentCredits.value, 'type', 'tv_show'),
      documentary: countMatches(departmentCredits.value, 'type', 'documentary'),
      talk_show: countMatches(departmentCredits.value, 'type', 'talk_show'),
    }
  }

  return {
    all: departmentCredits.value.length,
    feature: countMatches(departmentCredits.value, 'type', 'feature'),
    short: countMatches(departmentCredits.value, 'type', 'short'),
    documentary: countMatches(departmentCredits.value, 'type', 'documentary'),
  }
})

function filterCreditsByType(entries, typeValue) {
  if (typeValue === 'all') {
    return entries
  }
  return entries.filter((entry) => entry.type === typeValue)
}

const typeScopedCredits = computed(() => (
  filterCreditsByType(departmentCredits.value, selectedType.value)
))

const currentReleaseStatusCounts = computed(() => {
  if (selectedMedium.value === 'tv') {
    return {
      all: typeScopedCredits.value.length,
      upcoming: countMatches(typeScopedCredits.value, 'release_status', 'upcoming'),
      ongoing: countMatches(typeScopedCredits.value, 'release_status', 'ongoing'),
      ended: countMatches(typeScopedCredits.value, 'release_status', 'ended'),
    }
  }

  return {
    all: typeScopedCredits.value.length,
    released: countMatches(typeScopedCredits.value, 'release_status', 'released'),
    unreleased: countMatches(typeScopedCredits.value, 'release_status', 'unreleased'),
  }
})

const baseCredits = computed(() => typeScopedCredits.value.filter((credit) => {
  const matchesRelease = (
    selectedReleaseStatus.value === 'all'
      || credit.release_status === selectedReleaseStatus.value
  )
  return matchesRelease
}))

function countFilmCreditsByActivity(entries, matcher, loadedBySlug) {
  return entries.reduce((total, credit) => {
    const slugValue = creditFilmSlug(credit)
    if (!slugValue || !loadedBySlug.value[slugValue]) {
      return total
    }
    return matcher(slugValue) ? total + 1 : total
  }, 0)
}

function countShowCreditsByActivity(entries, matcher, loadedBySlug) {
  return entries.reduce((total, credit) => {
    const slugValue = creditShowSlug(credit)
    if (!slugValue || !loadedBySlug.value[slugValue]) {
      return total
    }
    return matcher(slugValue) ? total + 1 : total
  }, 0)
}

const filmActivityCounts = computed(() => {
  if (
    selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || baseCredits.value.length === 0
  ) {
    return null
  }

  const watched = countFilmCreditsByActivity(
    baseCredits.value,
    (slugValue) => Boolean(filmWatchedStates.value[slugValue]),
    filmWatchedLoadedBySlug,
  )
  const favourited = countFilmCreditsByActivity(
    baseCredits.value,
    (slugValue) => Boolean(filmFavouriteStates.value[slugValue]),
    filmFavouriteLoadedBySlug,
  )
  const watchlisted = countFilmCreditsByActivity(
    baseCredits.value,
    (slugValue) => Boolean(filmWatchlistStates.value[slugValue]),
    filmWatchlistLoadedBySlug,
  )
  const rated = countFilmCreditsByActivity(
    baseCredits.value,
    (slugValue) => normalizeRatingValue(filmRatingStates.value[slugValue]) !== null,
    filmRatingLoadedBySlug,
  )
  const unwatched = watched > 0
    ? countFilmCreditsByActivity(
      baseCredits.value,
      (slugValue) => !filmWatchedStates.value[slugValue],
      filmWatchedLoadedBySlug,
    )
    : 0

  return {
    all: baseCredits.value.length,
    watched,
    unwatched,
    watchlisted,
    rated,
    favourited,
  }
})

const showActivityCounts = computed(() => {
  if (
    selectedMedium.value !== 'tv'
    || !isAuthenticated.value
    || baseCredits.value.length === 0
  ) {
    return null
  }

  const watchlisted = countShowCreditsByActivity(
    baseCredits.value,
    (slugValue) => Boolean(showWatchlistStates.value[slugValue]),
    showWatchlistLoadedBySlug,
  )
  const rated = countShowCreditsByActivity(
    baseCredits.value,
    (slugValue) => normalizeRatingValue(showRatingStates.value[slugValue]) !== null,
    showRatingLoadedBySlug,
  )
  const favourited = countShowCreditsByActivity(
    baseCredits.value,
    (slugValue) => Boolean(showFavouriteStates.value[slugValue]),
    showFavouriteLoadedBySlug,
  )

  return {
    all: baseCredits.value.length,
    watchlisted,
    rated,
    favourited,
  }
})

const activityFilterOptions = computed(() => {
  if (!isAuthenticated.value) {
    return []
  }

  if (selectedMedium.value === 'tv') {
    if (!showActivityCounts.value) {
      return []
    }

    const options = [
      {
        value: 'all',
        label: `All (${showActivityCounts.value.all})`,
        active: selectedActivity.value === 'all',
      },
    ]

    if (showActivityCounts.value.watchlisted > 0) {
      options.push({
        value: 'watchlisted',
        label: `Watchlisted (${showActivityCounts.value.watchlisted})`,
        active: selectedActivity.value === 'watchlisted',
      })
    }

    if (showActivityCounts.value.rated > 0) {
      options.push({
        value: 'rated',
        label: `Rated (${showActivityCounts.value.rated})`,
        active: selectedActivity.value === 'rated',
      })
    }

    if (showActivityCounts.value.favourited > 0) {
      options.push({
        value: 'favourited',
        label: `Favourited (${showActivityCounts.value.favourited})`,
        active: selectedActivity.value === 'favourited',
      })
    }

    return options
  }

  if (!filmActivityCounts.value) {
    return []
  }

  const options = [
    {
      value: 'all',
      label: `All (${filmActivityCounts.value.all})`,
      active: selectedActivity.value === 'all',
    },
  ]

  if (filmActivityCounts.value.watched > 0) {
    options.push({
      value: 'watched',
      label: `Watched (${filmActivityCounts.value.watched})`,
      active: selectedActivity.value === 'watched',
    })
  }

  if (filmActivityCounts.value.unwatched > 0) {
    options.push({
      value: 'unwatched',
      label: `Unwatched (${filmActivityCounts.value.unwatched})`,
      active: selectedActivity.value === 'unwatched',
    })
  }

  if (filmActivityCounts.value.watchlisted > 0) {
    options.push({
      value: 'watchlisted',
      label: `Watchlisted (${filmActivityCounts.value.watchlisted})`,
      active: selectedActivity.value === 'watchlisted',
    })
  }

  if (filmActivityCounts.value.rated > 0) {
    options.push({
      value: 'rated',
      label: `Rated (${filmActivityCounts.value.rated})`,
      active: selectedActivity.value === 'rated',
    })
  }

  if (filmActivityCounts.value.favourited > 0) {
    options.push({
      value: 'favourited',
      label: `Favourited (${filmActivityCounts.value.favourited})`,
      active: selectedActivity.value === 'favourited',
    })
  }

  return options
})

const activityScopedCredits = computed(() => {
  if (selectedActivity.value === 'all') {
    return baseCredits.value
  }

  if (selectedMedium.value === 'tv') {
    return baseCredits.value.filter((credit) => {
      const slugValue = creditShowSlug(credit)
      if (!slugValue) {
        return false
      }

      switch (selectedActivity.value) {
        case 'watchlisted':
          return showWatchlistLoadedBySlug.value[slugValue] && Boolean(showWatchlistStates.value[slugValue])
        case 'rated':
          return (
            showRatingLoadedBySlug.value[slugValue]
            && normalizeRatingValue(showRatingStates.value[slugValue]) !== null
          )
        case 'favourited':
          return showFavouriteLoadedBySlug.value[slugValue] && Boolean(showFavouriteStates.value[slugValue])
        default:
          return true
      }
    })
  }

  if (selectedMedium.value !== 'film') {
    return baseCredits.value
  }

  return baseCredits.value.filter((credit) => {
    const slugValue = creditFilmSlug(credit)
    if (!slugValue) {
      return false
    }

    switch (selectedActivity.value) {
      case 'watched':
        return filmWatchedLoadedBySlug.value[slugValue] && Boolean(filmWatchedStates.value[slugValue])
      case 'unwatched':
        return filmWatchedLoadedBySlug.value[slugValue] && !filmWatchedStates.value[slugValue]
      case 'watchlisted':
        return filmWatchlistLoadedBySlug.value[slugValue] && Boolean(filmWatchlistStates.value[slugValue])
      case 'rated':
        return (
          filmRatingLoadedBySlug.value[slugValue]
          && normalizeRatingValue(filmRatingStates.value[slugValue]) !== null
        )
      case 'favourited':
        return filmFavouriteLoadedBySlug.value[slugValue] && Boolean(filmFavouriteStates.value[slugValue])
      default:
        return true
    }
  })
})

const filmographyHeading = computed(() => {
  switch (selectedDepartment.value) {
    case 'directing':
      return 'Filmography - Directing'
    case 'writing':
      return 'Filmography - Writing'
    case 'crew':
      return 'Filmography - Crew member'
    case 'acting':
    default:
      return 'Filmography - Acting'
  }
})

const activeFiltersSummary = computed(() => {
  if (selectedMedium.value === 'tv') {
    const departmentLabel = {
      acting: 'an actor',
      directing: 'a director',
      writing: 'a writer',
      crew: 'a crew member',
    }[selectedDepartment.value] || 'an actor'

    const typeLabel = {
      all: 'TV shows',
      tv_show: 'TV shows',
      documentary: 'documentaries',
      talk_show: 'talk shows',
    }[selectedType.value] || 'TV shows'

    let scope = typeLabel

    if (selectedReleaseStatus.value === 'all') {
      scope = `all ${typeLabel}`
    } else if (selectedType.value === 'all') {
      scope = `${selectedReleaseStatus.value} TV shows`
    } else {
      scope = `${selectedReleaseStatus.value} ${typeLabel}`
    }

    if (!isAuthenticated.value || selectedActivity.value === 'all') {
      return `As ${departmentLabel} in ${scope}`
    }

    const activityLabel = {
      watchlisted: 'Watchlisted',
      rated: 'Rated',
      favourited: 'Favourited',
    }[selectedActivity.value]

    return activityLabel
      ? `As ${departmentLabel} in ${scope} (${activityLabel})`
      : `As ${departmentLabel} in ${scope}`
  }

  const departmentLabel = {
    acting: 'an actor',
    directing: 'a director',
    writing: 'a writer',
    crew: 'a crew member',
  }[selectedDepartment.value] || 'an actor'

  const typeLabel = {
    all: 'films',
    feature: 'feature films',
    short: 'short films',
    documentary: 'documentaries',
  }[selectedType.value] || 'films'

  let scope = typeLabel

  if (selectedReleaseStatus.value === 'all') {
    scope = `all ${typeLabel}`
  } else if (selectedType.value === 'all') {
    scope = `${selectedReleaseStatus.value} films`
  } else {
    scope = `${selectedReleaseStatus.value} ${typeLabel}`
  }

  if (!isAuthenticated.value || selectedActivity.value === 'all') {
    return `As ${departmentLabel} in ${scope}`
  }

  const activityLabel = {
    watched: 'Watched',
    unwatched: 'Unwatched',
    watchlisted: 'Watchlisted',
    rated: 'Rated',
    favourited: 'Favourited',
  }[selectedActivity.value]

  return activityLabel
    ? `As ${departmentLabel} in ${scope} (${activityLabel})`
    : `As ${departmentLabel} in ${scope}`
})

const typeFilterOptions = computed(() => {
  if (selectedMedium.value === 'tv') {
    const specificOptions = [
      ...(currentTypeCounts.value.tv_show > 0
        ? [{ value: 'tv_show', label: `TV Show (${currentTypeCounts.value.tv_show})` }]
        : []),
      ...(currentTypeCounts.value.documentary > 0
        ? [{ value: 'documentary', label: `Documentary (${currentTypeCounts.value.documentary})` }]
        : []),
      ...(currentTypeCounts.value.talk_show > 0
        ? [{ value: 'talk_show', label: `Talk show (${currentTypeCounts.value.talk_show})` }]
        : []),
    ]

    if (specificOptions.length === 1) {
      return specificOptions.map((option) => ({ ...option, active: true }))
    }

    return [
      { value: 'all', label: `All (${currentTypeCounts.value.all})`, active: selectedType.value === 'all' },
      ...specificOptions.map((option) => ({
        ...option,
        active: selectedType.value === option.value,
      })),
    ]
  }

  const specificOptions = [
    ...(currentTypeCounts.value.feature > 0
      ? [{ value: 'feature', label: `Feature film (${currentTypeCounts.value.feature})` }]
      : []),
    ...(currentTypeCounts.value.short > 0
      ? [{ value: 'short', label: `Short film (${currentTypeCounts.value.short})` }]
      : []),
    ...(currentTypeCounts.value.documentary > 0
      ? [{ value: 'documentary', label: `Documentary (${currentTypeCounts.value.documentary})` }]
      : []),
  ]

  if (specificOptions.length === 1) {
    return specificOptions.map((option) => ({ ...option, active: true }))
  }

  return [
    { value: 'all', label: `All (${currentTypeCounts.value.all})`, active: selectedType.value === 'all' },
    ...specificOptions.map((option) => ({
      ...option,
      active: selectedType.value === option.value,
    })),
  ]
})

const releaseStatusFilterOptions = computed(() => {
  if (selectedMedium.value === 'tv') {
    const specificOptions = [
      ...(currentReleaseStatusCounts.value.upcoming > 0
        ? [{ value: 'upcoming', label: `Upcoming (${currentReleaseStatusCounts.value.upcoming})` }]
        : []),
      ...(currentReleaseStatusCounts.value.ongoing > 0
        ? [{ value: 'ongoing', label: `Ongoing (${currentReleaseStatusCounts.value.ongoing})` }]
        : []),
      ...(currentReleaseStatusCounts.value.ended > 0
        ? [{ value: 'ended', label: `Ended (${currentReleaseStatusCounts.value.ended})` }]
        : []),
    ]

    if (specificOptions.length === 1) {
      return specificOptions.map((option) => ({ ...option, active: true }))
    }

    return [
      { value: 'all', label: `All (${currentReleaseStatusCounts.value.all})`, active: selectedReleaseStatus.value === 'all' },
      ...specificOptions.map((option) => ({
        ...option,
        active: selectedReleaseStatus.value === option.value,
      })),
    ]
  }

  const specificOptions = [
    ...(currentReleaseStatusCounts.value.released > 0
      ? [{ value: 'released', label: `Released (${currentReleaseStatusCounts.value.released})` }]
      : []),
    ...(currentReleaseStatusCounts.value.unreleased > 0
      ? [{ value: 'unreleased', label: `Upcoming (${currentReleaseStatusCounts.value.unreleased})` }]
      : []),
  ]

  if (specificOptions.length === 1) {
    return specificOptions.map((option) => ({ ...option, active: true }))
  }

  return [
    { value: 'all', label: `All (${currentReleaseStatusCounts.value.all})`, active: selectedReleaseStatus.value === 'all' },
    ...specificOptions.map((option) => ({
      ...option,
      active: selectedReleaseStatus.value === option.value,
    })),
  ]
})

const releaseStatusGroupTitle = computed(() => (
  selectedMedium.value === 'tv' ? 'Status:' : 'Release status:'
))

const filterGroups = computed(() => [
  {
    key: 'department',
    title: 'Department:',
    options: selectedMedium.value === 'tv'
      ? [
          ...(departmentCounts.value.acting > 0
            ? [{ value: 'acting', label: `Acting (${departmentCounts.value.acting})`, active: selectedDepartment.value === 'acting' }]
            : []),
        ]
      : [
          ...(departmentCounts.value.acting > 0
            ? [{ value: 'acting', label: `Acting (${departmentCounts.value.acting})`, active: selectedDepartment.value === 'acting' }]
            : []),
          ...(departmentCounts.value.directing > 0
            ? [{ value: 'directing', label: `Directing (${departmentCounts.value.directing})`, active: selectedDepartment.value === 'directing' }]
            : []),
          ...(departmentCounts.value.writing > 0
            ? [{ value: 'writing', label: `Writing (${departmentCounts.value.writing})`, active: selectedDepartment.value === 'writing' }]
            : []),
          ...(departmentCounts.value.crew > 0
            ? [{ value: 'crew', label: `Crew member (${departmentCounts.value.crew})`, active: selectedDepartment.value === 'crew' }]
            : []),
        ],
  },
  {
    key: 'type',
    title: 'Type:',
    options: typeFilterOptions.value,
  },
  {
    key: 'release-status',
    title: releaseStatusGroupTitle.value,
    options: releaseStatusFilterOptions.value,
  },
  ...((selectedMedium.value === 'film' || selectedMedium.value === 'tv') && isAuthenticated.value && activityFilterOptions.value.length
    ? [{
        key: 'activity',
        title: 'Your activity',
        options: activityFilterOptions.value,
      }]
    : []),
])

const filterModalRows = computed(() => {
  const groups = filterGroups.value
  return [
    { key: 'department-type', groups: groups.slice(0, 2), full: false },
    { key: 'release-status', groups: groups.slice(2, 3), full: false },
    ...groups.slice(3).map((group) => ({
      key: group.key,
      groups: [group],
      full: true,
    })),
  ].filter((row) => row.groups.length)
})

const filterButtonLabel = computed(() => {
  return 'Filters'
})

const filtersModalOpen = ref(false)
const filmographyFilter = ref('')
const filmographyFilterPlaceholder = computed(() => (
  selectedMedium.value === 'tv'
    ? 'Filter show by title, character, actor name or start year...'
    : 'Filter by title, character, person or release year...'
))

const normalizedFilmographyFilter = computed(() => filmographyFilter.value.trim().toLowerCase())

const filteredCredits = computed(() => {
  if (normalizedFilmographyFilter.value.length < 2) {
    return activityScopedCredits.value
  }

  return activityScopedCredits.value.filter((credit) => {
    const haystacks = [
      credit.title,
      credit.character_name,
      credit.year,
      ...(credit.directors || []).map((director) => director.name),
      ...(credit.stars || []).map((star) => star.name),
      ...(credit.main_cast || []).map((castMember) => castMember.name),
    ]

    return haystacks.some((value) => String(value || '').toLowerCase().includes(normalizedFilmographyFilter.value))
  })
})

const visibleCredits = computed(() => {
  const items = [...filteredCredits.value]

  const numericYear = (value) => {
    const parsed = Number.parseInt(String(value || ''), 10)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  const titleSortKey = (credit) => String(credit?.title || '').replace(/^the\s+/i, '')
  const compareTitles = (left, right) => (
    titleSortKey(left).localeCompare(titleSortKey(right), undefined, { sensitivity: 'base' })
    || String(left.title || '').localeCompare(String(right.title || ''), undefined, { sensitivity: 'base' })
  )

  switch (sortMode.value) {
    case 'title-asc':
      return items.sort(compareTitles)
    case 'title-desc':
      return items.sort((left, right) => compareTitles(right, left))
    case 'oldest':
      return items.sort((left, right) => {
        const yearDiff = numericYear(left.year) - numericYear(right.year)
        if (yearDiff !== 0) {
          return yearDiff
        }
        return compareTitles(left, right)
      })
    case 'newest':
    default:
      return items.sort((left, right) => {
        const yearDiff = numericYear(right.year) - numericYear(left.year)
        if (yearDiff !== 0) {
          return yearDiff
        }
        return compareTitles(left, right)
      })
  }
})

const noCreditsMessage = computed(() => (
  !hasLoadedPersonPayload.value
    ? (selectedMedium.value === 'tv' ? 'Loading shows...' : 'Loading films...')
    : activityScopedCredits.value.length === 0
    ? (selectedMedium.value === 'tv' ? 'No shows available.' : 'No films available.')
    : (selectedMedium.value === 'tv' ? 'No shows found.' : 'No films found.')
))

function setSortMode(mode) {
  sortMode.value = mode
}

function setViewMode(mode) {
  currentViewMode.value = mode
  storePersonViewMode(mode)
}

function handleViewModeClick(button) {
  if (!button.enabled) {
    return
  }
  setViewMode(button.key)
}

function getStoredPersonViewMode() {
  if (typeof window === 'undefined') return '1'

  try {
    const storedMode = window.sessionStorage.getItem(personViewModeStorageKey)
    return ['1', '2', '3', '4'].includes(storedMode) ? storedMode : '1'
  } catch {
    return '1'
  }
}

function storePersonViewMode(mode) {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(personViewModeStorageKey, mode)
  } catch {
    // Session storage is a convenience preference; ignore browser storage failures.
  }
}

function defaultTypeForDepartment(departmentValue) {
  const entries = (
    selectedMedium.value === 'tv'
      ? (showographyByDepartment.value[departmentValue] || [])
      : (filmographyByDepartment.value[departmentValue] || [])
  )
  const specificTypes = selectedMedium.value === 'tv'
    ? [
        ...(countMatches(entries, 'type', 'tv_show') > 0 ? ['tv_show'] : []),
        ...(countMatches(entries, 'type', 'documentary') > 0 ? ['documentary'] : []),
        ...(countMatches(entries, 'type', 'talk_show') > 0 ? ['talk_show'] : []),
      ]
    : [
        ...(countMatches(entries, 'type', 'feature') > 0 ? ['feature'] : []),
        ...(countMatches(entries, 'type', 'short') > 0 ? ['short'] : []),
        ...(countMatches(entries, 'type', 'documentary') > 0 ? ['documentary'] : []),
      ]

  const preferredType = selectedMedium.value === 'tv' ? 'tv_show' : 'feature'
  if (specificTypes.includes(preferredType)) {
    return preferredType
  }
  if (specificTypes.length === 1) {
    return specificTypes[0]
  }
  return 'all'
}

function defaultReleaseStatusForEntries(entries) {
  if (selectedMedium.value === 'tv') {
    return 'all'
  }

  const releasedCount = countMatches(entries, 'release_status', 'released')
  const unreleasedCount = countMatches(entries, 'release_status', 'unreleased')

  if (releasedCount > 0) {
    return 'released'
  }
  if (unreleasedCount > 0) {
    return 'unreleased'
  }
  return 'all'
}

function departmentFromSection(section) {
  return supportedDepartmentSections.includes(section) ? section : 'acting'
}

function sectionFromDepartment(department) {
  return supportedDepartmentSections.includes(department) ? department : null
}

function buildMediumPath(medium, department = 'acting') {
  if (medium === 'tv') {
    return `${basePath.value}/TV`
  }
  const section = sectionFromDepartment(department)
  return section ? `${basePath.value}/${section}` : basePath.value
}

const filmMediumPath = computed(() => (
  buildMediumPath('film', selectedMedium.value === 'film' ? selectedDepartment.value : 'acting')
))

const tvMediumPath = computed(() => buildMediumPath('tv'))
const listsPath = computed(() => `${basePath.value}/lists`)
const commentsPath = computed(() => `${basePath.value}/comments`)
const filmographyPath = computed(() => buildDepartmentPath(selectedDepartment.value))
const personListsHeading = computed(() => `Lists with this person (${personListsCount.value})`)

function slugifyText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function personListDetailPath(personList) {
  if (personList?.is_curated && personList.url) return personList.url
  const listId = Number.parseInt(String(personList?.id || ''), 10)
  if (!listId) {
    return '/lists/person'
  }
  const titleSlug = slugifyText(personList?.title || '') || 'list'
  return `/lists/person/${listId}-${titleSlug}`
}

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}

function normalizePersonList(personList) {
  return {
    ...personList,
    ownerHref: buildFilmsomniacPath(personList?.owner_name),
  }
}

const filteredPersonLists = computed(() => {
  const query = personListsFilter.value.trim().toLowerCase()
  if (!query) {
    return personLists.value
  }

  return personLists.value.filter((personList) => (
    String(personList.title || '').toLowerCase().includes(query)
    || String(personList.owner_name || '').toLowerCase().includes(query)
  ))
})
const showPersonListsPagination = computed(() => (
  !personListsFilter.value.trim() && personListsNumPages.value > 1
))
const canGoToPreviousPersonListsPage = computed(() => personListsPageNumber.value > 1)
const canGoToNextPersonListsPage = computed(() => personListsPageNumber.value < personListsNumPages.value)
const personListsPages = computed(() => (
  compactPaginationItems(personListsPageNumber.value, personListsNumPages.value)
))
const selectedPersonListLabel = computed(() => {
  const selectedId = String(personListSelectedListId.value || '').trim()
  if (!selectedId) {
    return 'Choose list...'
  }
  const selectedList = personListOptions.value.find((entry) => String(entry.id) === selectedId)
  return selectedList?.title || 'Choose list...'
})

function buildDepartmentPath(department) {
  return buildMediumPath(selectedMedium.value, department)
}

function syncRouteWithDepartment(department, replace = false) {
  if (!personSlug.value) {
    return
  }

  const targetPath = buildDepartmentPath(department)
  if (route.fullPath === targetPath) {
    return
  }

  const navigate = replace ? router.replace : router.push
  navigate(targetPath).catch(() => {
    // Ignore duplicate navigation and routing noise from state synchronization.
  })
}

function normalizeDepartmentFromRoute() {
  if (routeSectionToMedium(personSection.value) === 'tv') {
    return 'acting'
  }
  const requestedDepartment = departmentFromSection(personSection.value)
  if (requestedDepartment === 'acting') {
    return departmentCounts.value.acting > 0 ? 'acting' : firstAvailableDepartment.value
  }
  if (departmentCounts.value[requestedDepartment] > 0) {
    return requestedDepartment
  }
  return firstAvailableDepartment.value
}

function setDepartmentSelection(value) {
  selectedDepartment.value = value
  selectedType.value = defaultTypeForDepartment(value)
  const sourceEntries = selectedMedium.value === 'tv'
    ? (showographyByDepartment.value[value] || [])
    : (filmographyByDepartment.value[value] || [])
  selectedReleaseStatus.value = defaultReleaseStatusForEntries(
    filterCreditsByType(sourceEntries, selectedType.value),
  )
  if (selectedMedium.value !== 'film') {
    selectedActivity.value = 'all'
  }
}

function shouldAddDetailsTopGap(credit, rowType) {
  const hasOverview = Boolean(credit?.overview)
  const hasDirectors = Boolean(credit?.directors?.length)
  const hasStars = Boolean(credit?.stars?.length)

  if (hasOverview) {
    return false
  }

  if (rowType === 'directors') {
    return hasDirectors
  }

  if (rowType === 'stars') {
    return !hasDirectors && hasStars
  }

  return false
}

function buildAuthHeaders() {
  return auth.state.access
    ? { Authorization: `Bearer ${auth.state.access}` }
    : {}
}

function personApiIdentifier() {
  return personDatabaseId.value || personId.value
}

function personApiUrl(path = '', params = {}) {
  const identifier = personApiIdentifier()
  const normalizedPath = path ? `/${String(path).replace(/^\/+/, '')}` : '/'
  const query = new URLSearchParams(params)
  if (personDatabaseId.value) {
    query.set('lookup', 'pk')
  }
  const queryString = query.toString()
  return `/api/v1/people/${identifier}${normalizedPath}${queryString ? `?${queryString}` : ''}`
}

async function loadPersonFavouriteStatus() {
  if (
    !personId.value
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || personFavouriteLoaded.value
    || personFavouritePending.value
  ) {
    return
  }

  personFavouritePending.value = true
  try {
    const response = await fetch(personApiUrl('favourite'), {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    personFavourited.value = Boolean(data.favourited)
    personFavouriteLoaded.value = true
  } catch {
    // Keep the control in the default state if the favourite status request fails.
  } finally {
    personFavouritePending.value = false
  }
}

async function togglePersonFavourite() {
  if (
    !personId.value
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || personFavouritePending.value
  ) {
    return
  }

  personFavouritePending.value = true
  try {
    const response = await fetch(personApiUrl('favourite'), {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'P' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    personFavourited.value = Boolean(data.favourited)
    personFavouriteLoaded.value = true
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    personFavouritePending.value = false
  }
}

function resetPersonListModalForm() {
  personListModalError.value = ''
  personListCreateMode.value = false
  personListSelectedListId.value = ''
  personListPickerOpen.value = false
  personListCreateTitle.value = ''
  personListCreateDescription.value = ''
  personListCreatePublic.value = true
}

function closePersonListModal(force = false) {
  if (personListModalPending.value && !force) {
    return
  }
  personListModalOpen.value = false
  personListModalLoading.value = false
  resetPersonListModalForm()
}

function openPersonListCreateMode() {
  personListCreateMode.value = true
  personListModalError.value = ''
  personListPickerOpen.value = false
}

function closePersonListCreateMode() {
  personListCreateMode.value = false
  personListModalError.value = ''
}

function selectPersonList(listId) {
  personListSelectedListId.value = String(listId || '')
  personListPickerOpen.value = false
}

async function loadPersonListsPage(pageNumber = personListsPageNumber.value) {
  if (!personId.value || typeof fetch !== 'function') {
    return
  }

  const normalizedPageNumber = Math.max(1, Number.parseInt(pageNumber, 10) || 1)
  personListsPageLoading.value = true
  personListsPageLoaded.value = false
  personListsPageError.value = ''
  try {
    const response = isAuthenticated.value
      ? await fetch(personApiUrl('lists/page', { page: String(normalizedPageNumber) }), {
        headers: buildAuthHeaders(),
      })
      : await fetch(personApiUrl('lists/page', { page: String(normalizedPageNumber) }))
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      personLists.value = []
      personListsCount.value = 0
      personListsPageNumber.value = 1
      personListsNumPages.value = 1
      personListsPageLoaded.value = true
      return
    }
    if (!response.ok) {
      personListsPageError.value = 'Unable to load lists right now.'
      personLists.value = []
      personListsCount.value = 0
      personListsPageNumber.value = 1
      personListsNumPages.value = 1
      personListsPageLoaded.value = true
      return
    }

    const data = await response.json()
    personLists.value = Array.isArray(data.results) ? data.results.map(normalizePersonList) : []
    personListsCount.value = Number(data.count) || personLists.value.length
    personListsPageNumber.value = Number.parseInt(data.page, 10) || normalizedPageNumber
    personListsNumPages.value = Math.max(1, Number.parseInt(data.num_pages, 10) || 1)
    personListsPageLoaded.value = true
  } catch {
    personListsPageError.value = 'Unable to load lists right now.'
    personLists.value = []
    personListsCount.value = 0
    personListsPageNumber.value = 1
    personListsNumPages.value = 1
    personListsPageLoaded.value = true
  } finally {
    personListsPageLoading.value = false
  }
}

function goToPersonListsPage(pageNumber) {
  const normalizedPageNumber = Number.parseInt(pageNumber, 10) || 1
  if (
    personListsPageLoading.value ||
    normalizedPageNumber < 1 ||
    normalizedPageNumber > personListsNumPages.value ||
    normalizedPageNumber === personListsPageNumber.value
  ) {
    return
  }
  loadPersonListsPage(normalizedPageNumber)
}

async function openPersonListModal() {
  if (!personId.value || !isAuthenticated.value || personListModalPending.value || typeof fetch !== 'function') {
    return
  }

  personListModalOpen.value = true
  personListModalLoading.value = true
  resetPersonListModalForm()
  try {
    const response = await fetch(personApiUrl('lists'), {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closePersonListModal(true)
      return
    }
    if (!response.ok) {
      personListModalError.value = 'Unable to load your lists right now.'
      personListOptions.value = []
      return
    }

    const data = await response.json()
    personListOptions.value = Array.isArray(data.results) ? data.results : []
  } catch {
    personListModalError.value = 'Unable to load your lists right now.'
    personListOptions.value = []
  } finally {
    personListModalLoading.value = false
  }
}

async function submitPersonListSelection() {
  if (
    !personId.value
    || !personListSelectedListId.value
    || personListModalPending.value
    || typeof fetch !== 'function'
  ) {
    return
  }

  personListModalPending.value = true
  personListModalError.value = ''
  try {
    const response = await fetch(personApiUrl('lists'), {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ list_id: personListSelectedListId.value }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closePersonListModal(true)
      return
    }

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      personListModalError.value = data.list_id || 'Unable to add this person to the list right now.'
      return
    }

    closePersonListModal(true)
    await loadPersonListsPage()
  } catch {
    personListModalError.value = 'Unable to add this person to the list right now.'
  } finally {
    personListModalPending.value = false
  }
}

async function createPersonList() {
  if (!personId.value || personListModalPending.value || typeof fetch !== 'function') {
    return
  }

  personListModalPending.value = true
  personListModalError.value = ''
  try {
    const response = await fetch(personApiUrl('lists/create'), {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: personListCreateTitle.value,
        description: personListCreateDescription.value,
        public: personListCreatePublic.value,
      }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closePersonListModal(true)
      return
    }

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      personListModalError.value = data.title || 'Unable to create the list right now.'
      return
    }

    closePersonListModal(true)
    await loadPersonListsPage()
  } catch {
    personListModalError.value = 'Unable to create the list right now.'
  } finally {
    personListModalPending.value = false
  }
}

function normalizeRatingValue(value) {
  const parsedValue = Number.parseInt(String(value ?? ''), 10)
  if (Number.isNaN(parsedValue) || parsedValue < 1 || parsedValue > 10) {
    return null
  }
  return parsedValue
}

function creditFilmSlug(credit) {
  const filmUrl = String(credit?.film_url || '')
  const match = filmUrl.match(/^\/films\/([^/?#]+)/)
  return match ? match[1] : null
}

function creditShowSlug(credit) {
  const showUrl = String(credit?.film_url || '')
  const match = showUrl.match(/^\/shows\/([^/?#]+)/)
  return match ? match[1] : null
}

function creditFilmId(credit) {
  return credit?.id ? Number(credit.id) : null
}

function creditShowId(credit) {
  return credit?.id ? Number(credit.id) : null
}

const CREDIT_OVERVIEW_CLIP_LIMIT = 200

function creditOverviewKey(credit) {
  if (selectedMedium.value === 'tv') {
    return creditShowSlug(credit) || String(credit?.film_url || credit?.title || '')
  }
  return creditFilmSlug(credit) || String(credit?.film_url || credit?.title || '')
}

function isCreditOverviewExpanded(credit) {
  const key = creditOverviewKey(credit)
  return Boolean(key && expandedCreditOverviewIds.value[key])
}

function hasExpandableCreditOverview(credit) {
  return hasExpandableOverview(credit?.overview || '', CREDIT_OVERVIEW_CLIP_LIMIT)
}

function displayedCreditOverview(credit) {
  const overview = String(credit?.overview || '').trim()
  if (!hasExpandableCreditOverview(credit) || isCreditOverviewExpanded(credit)) {
    return overview
  }
  return clipOverviewWithSuffix(overview, CREDIT_OVERVIEW_CLIP_LIMIT, '')
}

function toggleCreditOverview(credit) {
  const key = creditOverviewKey(credit)
  if (!key) {
    return
  }

  expandedCreditOverviewIds.value = {
    ...expandedCreditOverviewIds.value,
    [key]: !expandedCreditOverviewIds.value[key],
  }
}

function isCreditFavourited(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    return slugValue ? Boolean(showFavouriteStates.value[slugValue]) : false
  }
  const slugValue = creditFilmSlug(credit)
  return slugValue ? Boolean(filmFavouriteStates.value[slugValue]) : false
}

function isCreditWatchlisted(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    return slugValue ? Boolean(showWatchlistStates.value[slugValue]) : false
  }
  const slugValue = creditFilmSlug(credit)
  return slugValue ? Boolean(filmWatchlistStates.value[slugValue]) : false
}

function isCreditCalendared(credit) {
  if (selectedMedium.value !== 'tv') {
    return false
  }

  const slugValue = creditShowSlug(credit)
  return slugValue ? Boolean(showCalendarStates.value[slugValue]) : false
}

function isCreditFavouritePending(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    return slugValue ? Boolean(showFavouritePendingBySlug.value[slugValue]) : false
  }
  const slugValue = creditFilmSlug(credit)
  return slugValue ? Boolean(filmFavouritePendingBySlug.value[slugValue]) : false
}

function isCreditWatchlistPending(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    return slugValue ? Boolean(showWatchlistPendingBySlug.value[slugValue]) : false
  }
  const slugValue = creditFilmSlug(credit)
  return slugValue ? Boolean(filmWatchlistPendingBySlug.value[slugValue]) : false
}

function isCreditCalendarPending(credit) {
  if (selectedMedium.value !== 'tv') {
    return false
  }

  const slugValue = creditShowSlug(credit)
  return slugValue ? Boolean(showCalendarPendingBySlug.value[slugValue]) : false
}

function isCreditCalendarEnabled(credit) {
  if (selectedMedium.value !== 'tv') {
    return false
  }

  const slugValue = creditShowSlug(credit)
  if (!slugValue) {
    return false
  }

  return showCalendarEnabledBySlug.value[slugValue] !== false
}

function creditWatchlistButtonText(credit) {
  return isCreditWatchlisted(credit) ? 'Watchlisted' : 'Add to watchlist'
}

function creditWatchlistButtonShortText(credit) {
  return isCreditWatchlisted(credit) ? 'Watchlisted' : 'Watchlist'
}

function isCreditWatched(credit) {
  const slugValue = creditFilmSlug(credit)
  return slugValue ? Boolean(filmWatchedStates.value[slugValue]) : false
}

function isCreditWatchPending(credit) {
  const slugValue = creditFilmSlug(credit)
  return slugValue ? Boolean(filmWatchedPendingBySlug.value[slugValue]) : false
}

function creditWatchIconClass(credit) {
  return isCreditWatched(credit) ? 'ion-checkmark' : 'ion-plus'
}

function creditFavouriteButtonText(credit) {
  return isCreditFavourited(credit) ? 'Favourited' : 'Add to favourites'
}

function creditFavouriteButtonShortText(credit) {
  return isCreditFavourited(credit) ? 'Favourited' : 'Favourite'
}

function creditCalendarButtonText(credit) {
  return isCreditCalendared(credit) ? 'In calendar' : 'Add to calendar'
}

function creditUsesPlaceholderPoster(credit) {
  return !credit?.poster_url || credit.poster_url === filmPosterPlaceholder
}

function creditPosterUrl(credit) {
  return credit?.poster_url || filmPosterPlaceholder
}

function creditFilmRatingValue(credit) {
  const slugValue = creditFilmSlug(credit)
  return slugValue ? normalizeRatingValue(filmRatingStates.value[slugValue]) : null
}

function creditShowRatingValue(credit) {
  const slugValue = creditShowSlug(credit)
  return slugValue ? normalizeRatingValue(showRatingStates.value[slugValue]) : null
}

function creditFilmRatingText(credit) {
  const ratingValue = creditFilmRatingValue(credit)
  return ratingValue ? `${ratingValue} / 10` : '/ 10'
}

function creditShowRatingText(credit) {
  const ratingValue = creditShowRatingValue(credit)
  return ratingValue ? `${ratingValue} / 10` : '/ 10'
}

function creditFilmSummaryRatingStarClass(credit) {
  return [
    'rating-star',
    creditFilmRatingValue(credit) ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function creditShowSummaryRatingStarClass(credit) {
  return [
    'rating-star',
    creditShowRatingValue(credit) ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function previewCreditFilmRating(credit, star) {
  const slugValue = creditFilmSlug(credit)
  if (!slugValue) return
  filmRatingHoverBySlug.value = {
    ...filmRatingHoverBySlug.value,
    [slugValue]: star,
  }
}

function resetCreditFilmRatingPreview(credit) {
  const slugValue = creditFilmSlug(credit)
  if (!slugValue || !Object.prototype.hasOwnProperty.call(filmRatingHoverBySlug.value, slugValue)) {
    return
  }
  const nextValue = { ...filmRatingHoverBySlug.value }
  delete nextValue[slugValue]
  filmRatingHoverBySlug.value = nextValue
}

function previewCreditShowRating(credit, star) {
  const slugValue = creditShowSlug(credit)
  if (!slugValue) return
  showRatingHoverBySlug.value = {
    ...showRatingHoverBySlug.value,
    [slugValue]: star,
  }
}

function resetCreditShowRatingPreview(credit) {
  const slugValue = creditShowSlug(credit)
  if (!slugValue || !Object.prototype.hasOwnProperty.call(showRatingHoverBySlug.value, slugValue)) {
    return
  }
  const nextValue = { ...showRatingHoverBySlug.value }
  delete nextValue[slugValue]
  showRatingHoverBySlug.value = nextValue
}

function creditFilmRatingStarClass(credit, star) {
  const slugValue = creditFilmSlug(credit)
  const visibleRating = (
    (slugValue ? filmRatingHoverBySlug.value[slugValue] : 0)
    || creditFilmRatingValue(credit)
    || 0
  )

  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function creditShowRatingStarClass(credit, star) {
  const slugValue = creditShowSlug(credit)
  const visibleRating = (
    (slugValue ? showRatingHoverBySlug.value[slugValue] : 0)
    || creditShowRatingValue(credit)
    || 0
  )

  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function creditRatingValue(credit) {
  return selectedMedium.value === 'tv' ? creditShowRatingValue(credit) : creditFilmRatingValue(credit)
}

function creditRatingText(credit) {
  return selectedMedium.value === 'tv' ? creditShowRatingText(credit) : creditFilmRatingText(credit)
}

function creditSummaryRatingStarClass(credit) {
  return selectedMedium.value === 'tv'
    ? creditShowSummaryRatingStarClass(credit)
    : creditFilmSummaryRatingStarClass(credit)
}

function previewCreditRating(credit, star) {
  if (selectedMedium.value === 'tv') {
    previewCreditShowRating(credit, star)
    return
  }
  previewCreditFilmRating(credit, star)
}

function resetCreditRatingPreview(credit) {
  if (selectedMedium.value === 'tv') {
    resetCreditShowRatingPreview(credit)
    return
  }
  resetCreditFilmRatingPreview(credit)
}

function creditRatingStarClass(credit, star) {
  return selectedMedium.value === 'tv'
    ? creditShowRatingStarClass(credit, star)
    : creditFilmRatingStarClass(credit, star)
}

function isCreditRatingPending(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    return slugValue ? Boolean(showRatingPendingBySlug.value[slugValue]) : false
  }
  const slugValue = creditFilmSlug(credit)
  return slugValue ? Boolean(filmRatingPendingBySlug.value[slugValue]) : false
}

function creditRatingSlug(credit) {
  return selectedMedium.value === 'tv' ? creditShowSlug(credit) : creditFilmSlug(credit)
}

async function openCompactCreditFilmRatingPicker(credit) {
  if (
    !isAuthenticated.value
    || !['film', 'tv'].includes(selectedMedium.value)
    || !['1', '3'].includes(currentViewMode.value)
  ) {
    return
  }
  await loadCreditRatingStatus(credit)
  compactCreditFilmRatingPickerCredit.value = credit
}

function closeCompactCreditFilmRatingPicker() {
  if (compactCreditFilmRatingPickerCredit.value) {
    resetCreditRatingPreview(compactCreditFilmRatingPickerCredit.value)
  }
  compactCreditFilmRatingPickerCredit.value = null
}

async function selectCompactCreditFilmRating(ratingValue) {
  if (!compactCreditFilmRatingPickerCredit.value) return
  const credit = compactCreditFilmRatingPickerCredit.value
  const wasSaved = await setCreditRating(credit, ratingValue)
  if (wasSaved) {
    closeCompactCreditFilmRatingPicker()
  }
}

function applyFilter(groupKey, value) {
  switch (groupKey) {
    case 'department':
      setDepartmentSelection(value)
      syncRouteWithDepartment(value)
      loadPersonCredits({ departmentOverride: value })
      break
    case 'type':
      selectedType.value = value
      selectedReleaseStatus.value = defaultReleaseStatusForEntries(
        filterCreditsByType(departmentCredits.value, value),
      )
      break
    case 'release-status':
      selectedReleaseStatus.value = value
      break
    case 'activity':
      selectedActivity.value = value
      break
    default:
      break
  }
}

function toggleFilters() {
  filtersModalOpen.value = !filtersModalOpen.value
}

async function loadCreditFavouriteStatus(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    if (
      !slugValue
      || !isAuthenticated.value
      || typeof fetch !== 'function'
      || showFavouriteLoadedBySlug.value[slugValue]
      || showFavouritePendingBySlug.value[slugValue]
    ) {
      return
    }

    showFavouritePendingBySlug.value[slugValue] = true
    try {
      const response = await fetch(`/api/v1/shows/${slugValue}/favourite`, {
        headers: buildAuthHeaders(),
      })
      if (response.status === 401 || response.status === 403) {
        auth.clearTokens()
        return
      }
      if (!response.ok) {
        return
      }
      const data = await response.json()
      showFavouriteStates.value[slugValue] = Boolean(data.favourited)
      showFavouriteLoadedBySlug.value[slugValue] = true
    } catch {
      // Keep the control in the default state if the favourite status request fails.
    } finally {
      showFavouritePendingBySlug.value[slugValue] = false
    }
    return
  }

  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || filmFavouriteLoadedBySlug.value[slugValue]
    || filmFavouritePendingBySlug.value[slugValue]
  ) {
    return
  }

  filmFavouritePendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/favourite`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    filmFavouriteStates.value[slugValue] = Boolean(data.favourited)
    filmFavouriteLoadedBySlug.value[slugValue] = true
  } catch {
    // Keep the control in the default state if the favourite status request fails.
  } finally {
    filmFavouritePendingBySlug.value[slugValue] = false
  }
}

async function loadCreditWatchlistStatus(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    if (
      !slugValue
      || !isAuthenticated.value
      || typeof fetch !== 'function'
      || showWatchlistLoadedBySlug.value[slugValue]
      || showWatchlistPendingBySlug.value[slugValue]
    ) {
      return
    }

    showWatchlistPendingBySlug.value[slugValue] = true
    try {
      const response = await fetch(`/api/v1/shows/${slugValue}/watchlist`, {
        headers: buildAuthHeaders(),
      })
      if (response.status === 401 || response.status === 403) {
        auth.clearTokens()
        return
      }
      if (!response.ok) {
        return
      }
      const data = await response.json()
      showWatchlistStates.value[slugValue] = Boolean(data.watchlisted)
      showWatchlistLoadedBySlug.value[slugValue] = true
    } catch {
      // Keep the control in the default state if the watchlist status request fails.
    } finally {
      showWatchlistPendingBySlug.value[slugValue] = false
    }
    return
  }

  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || filmWatchlistLoadedBySlug.value[slugValue]
    || filmWatchlistPendingBySlug.value[slugValue]
  ) {
    return
  }

  filmWatchlistPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/watchlist`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    filmWatchlistStates.value[slugValue] = Boolean(data.watchlisted)
    filmWatchlistLoadedBySlug.value[slugValue] = true
  } catch {
    // Keep the control in the default state if the watchlist status request fails.
  } finally {
    filmWatchlistPendingBySlug.value[slugValue] = false
  }
}

async function loadCreditWatchStatus(credit) {
  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || filmWatchedLoadedBySlug.value[slugValue]
    || filmWatchedPendingBySlug.value[slugValue]
  ) {
    return
  }

  filmWatchedPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/watch`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    filmWatchedStates.value[slugValue] = Boolean(data.has_watched)
    filmWatchedLoadedBySlug.value[slugValue] = true
  } catch {
    // Keep the watch control in the default state if the watch status request fails.
  } finally {
    filmWatchedPendingBySlug.value[slugValue] = false
  }
}

async function loadCreditCalendarStatus(credit) {
  if (selectedMedium.value !== 'tv') {
    return
  }

  const slugValue = creditShowSlug(credit)
  if (
    !slugValue
    || typeof fetch !== 'function'
    || showCalendarLoadedBySlug.value[slugValue]
    || showCalendarPendingBySlug.value[slugValue]
  ) {
    return
  }

  showCalendarPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/shows/${slugValue}/calendar`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    showCalendarStates.value[slugValue] = Boolean(data.in_calendar)
    showCalendarEnabledBySlug.value[slugValue] = data.enabled !== false
    showCalendarLoadedBySlug.value[slugValue] = true
  } catch {
    // Keep the control in the default state if the calendar status request fails.
  } finally {
    showCalendarPendingBySlug.value[slugValue] = false
  }
}

async function loadCreditRatingStatus(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    if (
      !slugValue
      || !isAuthenticated.value
      || typeof fetch !== 'function'
      || showRatingLoadedBySlug.value[slugValue]
      || showRatingPendingBySlug.value[slugValue]
    ) {
      return
    }

    showRatingPendingBySlug.value[slugValue] = true
    try {
      const response = await fetch(`/api/v1/shows/${slugValue}/rating`, {
        headers: buildAuthHeaders(),
      })
      if (response.status === 401 || response.status === 403) {
        auth.clearTokens()
        return
      }
      if (!response.ok) {
        return
      }
      const data = await response.json()
      showRatingStates.value[slugValue] = normalizeRatingValue(data.rating)
      showRatingLoadedBySlug.value[slugValue] = true
    } catch {
      // Keep the rating display in the default state if the rating status request fails.
    } finally {
      showRatingPendingBySlug.value[slugValue] = false
    }
    return
  }

  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || filmRatingLoadedBySlug.value[slugValue]
    || filmRatingPendingBySlug.value[slugValue]
  ) {
    return
  }

  filmRatingPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/rating`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    filmRatingStates.value[slugValue] = normalizeRatingValue(data.rating)
    filmRatingLoadedBySlug.value[slugValue] = true
  } catch {
    // Keep the rating display in the default state if the rating status request fails.
  } finally {
    filmRatingPendingBySlug.value[slugValue] = false
  }
}

function uniqueCreditSlugs(credits, slugGetter) {
  const slugs = []
  const seen = new Set()
  credits.forEach((credit) => {
    const slugValue = slugGetter(credit)
    if (!slugValue || seen.has(slugValue)) {
      return
    }
    seen.add(slugValue)
    slugs.push(slugValue)
  })
  return slugs
}

function uniqueCreditIds(credits, idGetter) {
  const ids = []
  const seen = new Set()
  credits.forEach((credit) => {
    const idValue = idGetter(credit)
    if (!idValue || seen.has(idValue)) {
      return
    }
    seen.add(idValue)
    ids.push(idValue)
  })
  return ids
}

function filmCreditStatusLoaded(slugValue) {
  return Boolean(
    filmWatchedLoadedBySlug.value[slugValue]
      && filmWatchlistLoadedBySlug.value[slugValue]
      && filmFavouriteLoadedBySlug.value[slugValue]
      && filmRatingLoadedBySlug.value[slugValue],
  )
}

function filmCreditStatusPending(slugValue) {
  return Boolean(
    filmWatchedPendingBySlug.value[slugValue]
      || filmWatchlistPendingBySlug.value[slugValue]
      || filmFavouritePendingBySlug.value[slugValue]
      || filmRatingPendingBySlug.value[slugValue],
  )
}

function setFilmCreditStatusPending(slugValue, pending) {
  filmWatchedPendingBySlug.value[slugValue] = pending
  filmWatchlistPendingBySlug.value[slugValue] = pending
  filmFavouritePendingBySlug.value[slugValue] = pending
  filmRatingPendingBySlug.value[slugValue] = pending
}

function applyFilmCreditStatus(slugValue, status = {}) {
  filmWatchedStates.value[slugValue] = Boolean(status.has_watched)
  filmWatchlistStates.value[slugValue] = Boolean(status.watchlisted)
  filmFavouriteStates.value[slugValue] = Boolean(status.favourited)
  filmRatingStates.value[slugValue] = normalizeRatingValue(status.rating)
  filmWatchedLoadedBySlug.value[slugValue] = true
  filmWatchlistLoadedBySlug.value[slugValue] = true
  filmFavouriteLoadedBySlug.value[slugValue] = true
  filmRatingLoadedBySlug.value[slugValue] = true
}

function showCreditStatusLoaded(slugValue) {
  return Boolean(
    showWatchlistLoadedBySlug.value[slugValue]
      && showFavouriteLoadedBySlug.value[slugValue]
      && showRatingLoadedBySlug.value[slugValue],
  )
}

function showCreditStatusPending(slugValue) {
  return Boolean(
    showWatchlistPendingBySlug.value[slugValue]
      || showFavouritePendingBySlug.value[slugValue]
      || showRatingPendingBySlug.value[slugValue],
  )
}

function setShowCreditStatusPending(slugValue, pending) {
  showWatchlistPendingBySlug.value[slugValue] = pending
  showFavouritePendingBySlug.value[slugValue] = pending
  showRatingPendingBySlug.value[slugValue] = pending
}

function applyShowCreditStatus(slugValue, status = {}) {
  showWatchlistStates.value[slugValue] = Boolean(status.watchlisted)
  showFavouriteStates.value[slugValue] = Boolean(status.favourited)
  showRatingStates.value[slugValue] = normalizeRatingValue(status.rating)
  showWatchlistLoadedBySlug.value[slugValue] = true
  showFavouriteLoadedBySlug.value[slugValue] = true
  showRatingLoadedBySlug.value[slugValue] = true
}

function applyInitialCreditStatuses(payload = {}) {
  const filmStatuses = payload?.credit_statuses?.films || {}
  Object.values(filmographyByDepartment.value).forEach((entries) => {
    const safeEntries = Array.isArray(entries) ? entries : []
    safeEntries.forEach((credit) => {
      const slugValue = creditFilmSlug(credit)
      const idValue = creditFilmId(credit)
      if (slugValue && idValue && filmStatuses[String(idValue)]) {
        applyFilmCreditStatus(slugValue, filmStatuses[String(idValue)])
      }
    })
  })

  const showStatuses = payload?.credit_statuses?.shows || {}
  Object.values(showographyByDepartment.value).forEach((entries) => {
    const safeEntries = Array.isArray(entries) ? entries : []
    safeEntries.forEach((credit) => {
      const slugValue = creditShowSlug(credit)
      const idValue = creditShowId(credit)
      if (slugValue && idValue && showStatuses[String(idValue)]) {
        applyShowCreditStatus(slugValue, showStatuses[String(idValue)])
      }
    })
  })
}

async function loadCreditStatusesBatch(credits, medium) {
  if (!personId.value || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  const filmSlugs = medium === 'film'
    ? uniqueCreditSlugs(credits, creditFilmSlug).filter((slugValue) => (
      !filmCreditStatusLoaded(slugValue) && !filmCreditStatusPending(slugValue)
    ))
    : []
  const showSlugs = medium === 'tv'
    ? uniqueCreditSlugs(credits, creditShowSlug).filter((slugValue) => (
      !showCreditStatusLoaded(slugValue) && !showCreditStatusPending(slugValue)
    ))
    : []
  const filmIds = medium === 'film' ? uniqueCreditIds(credits, creditFilmId) : []
  const showIds = medium === 'tv' ? uniqueCreditIds(credits, creditShowId) : []

  if (!filmSlugs.length && !showSlugs.length) {
    return
  }

  filmSlugs.forEach((slugValue) => setFilmCreditStatusPending(slugValue, true))
  showSlugs.forEach((slugValue) => setShowCreditStatusPending(slugValue, true))

  try {
    const response = await fetch(personApiUrl('credit-statuses'), {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        film_slugs: filmSlugs,
        show_slugs: showSlugs,
        film_ids: filmIds,
        show_ids: showIds,
      }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    filmSlugs.forEach((slugValue) => {
      const credit = credits.find((entry) => creditFilmSlug(entry) === slugValue)
      const idValue = creditFilmId(credit)
      applyFilmCreditStatus(
        slugValue,
        data?.films_by_id?.[String(idValue)] || data?.films?.[slugValue] || {},
      )
    })
    showSlugs.forEach((slugValue) => {
      const credit = credits.find((entry) => creditShowSlug(entry) === slugValue)
      const idValue = creditShowId(credit)
      applyShowCreditStatus(
        slugValue,
        data?.shows_by_id?.[String(idValue)] || data?.shows?.[slugValue] || {},
      )
    })
  } catch {
    // Keep controls in their default state if the batch status request fails.
  } finally {
    filmSlugs.forEach((slugValue) => setFilmCreditStatusPending(slugValue, false))
    showSlugs.forEach((slugValue) => setShowCreditStatusPending(slugValue, false))
  }
}

function ensureVisibleActionStatusesLoaded() {
  loadCreditStatusesBatch(visibleCredits.value, selectedMedium.value)
}

function ensureFilmActivityStatusesLoaded() {
  if (selectedMedium.value !== 'film' || !isAuthenticated.value) {
    return
  }

  loadCreditStatusesBatch(baseCredits.value, 'film')
}

function ensureShowActivityStatusesLoaded() {
  if (selectedMedium.value !== 'tv' || !isAuthenticated.value) {
    return
  }

  loadCreditStatusesBatch(baseCredits.value, 'tv')
}

async function markCreditWatched(credit) {
  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || isCreditWatched(credit)
    || typeof fetch !== 'function'
    || filmWatchedPendingBySlug.value[slugValue]
  ) {
    return
  }

  filmWatchedPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/watch`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'P' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    filmWatchedStates.value[slugValue] = true
    filmWatchedLoadedBySlug.value[slugValue] = true
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    filmWatchedPendingBySlug.value[slugValue] = false
  }
}

async function toggleCreditWatchlist(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    if (
      !slugValue
      || !isAuthenticated.value
      || typeof fetch !== 'function'
      || showWatchlistPendingBySlug.value[slugValue]
    ) {
      return
    }

    showWatchlistPendingBySlug.value[slugValue] = true
    try {
      const response = await fetch(`/api/v1/shows/${slugValue}/watchlist`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source: 'P' }),
      })
      if (response.status === 401 || response.status === 403) {
        auth.clearTokens()
        return
      }
      if (!response.ok) {
        return
      }
      const data = await response.json()
      showWatchlistStates.value[slugValue] = Boolean(data.watchlisted)
      showWatchlistLoadedBySlug.value[slugValue] = true
    } catch {
      // Leave the current button state unchanged on network or API failure.
    } finally {
      showWatchlistPendingBySlug.value[slugValue] = false
    }
    return
  }

  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || filmWatchlistPendingBySlug.value[slugValue]
  ) {
    return
  }

  filmWatchlistPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/watchlist`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'P' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    filmWatchlistStates.value[slugValue] = Boolean(data.watchlisted)
    filmWatchlistLoadedBySlug.value[slugValue] = true
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    filmWatchlistPendingBySlug.value[slugValue] = false
  }
}

async function toggleCreditFavourite(credit) {
  if (selectedMedium.value === 'tv') {
    const slugValue = creditShowSlug(credit)
    if (
      !slugValue
      || !isAuthenticated.value
      || typeof fetch !== 'function'
      || showFavouritePendingBySlug.value[slugValue]
    ) {
      return
    }

    showFavouritePendingBySlug.value[slugValue] = true
    try {
      const response = await fetch(`/api/v1/shows/${slugValue}/favourite`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source: 'P' }),
      })
      if (response.status === 401 || response.status === 403) {
        auth.clearTokens()
        return
      }
      if (!response.ok) {
        return
      }
      const data = await response.json()
      showFavouriteStates.value[slugValue] = Boolean(data.favourited)
      showFavouriteLoadedBySlug.value[slugValue] = true
    } catch {
      // Leave the current button state unchanged on network or API failure.
    } finally {
      showFavouritePendingBySlug.value[slugValue] = false
    }
    return
  }

  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || filmFavouritePendingBySlug.value[slugValue]
  ) {
    return
  }

  filmFavouritePendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/favourite`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'P' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    filmFavouriteStates.value[slugValue] = Boolean(data.favourited)
    filmFavouriteLoadedBySlug.value[slugValue] = true
    if (data.favourited && !isCreditWatched(credit)) {
      await markCreditWatched(credit)
    }
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    filmFavouritePendingBySlug.value[slugValue] = false
  }
}

async function setCreditFilmRating(credit, ratingValue) {
  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || filmRatingPendingBySlug.value[slugValue]
  ) {
    return false
  }

  filmRatingPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/rating`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating: ratingValue, source: 'P' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return false
    }
    if (!response.ok) {
      return false
    }
    const data = await response.json()
    filmRatingStates.value[slugValue] = normalizeRatingValue(data.rating)
    filmRatingLoadedBySlug.value[slugValue] = true
    if (!isCreditWatched(credit)) {
      await markCreditWatched(credit)
    }
    resetCreditFilmRatingPreview(credit)
    return true
  } catch {
    // Leave the current rating unchanged on network or API failure.
    return false
  } finally {
    filmRatingPendingBySlug.value[slugValue] = false
  }
}

async function setCreditShowRating(credit, ratingValue) {
  const slugValue = creditShowSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'tv'
    || !isAuthenticated.value
    || typeof fetch !== 'function'
    || showRatingPendingBySlug.value[slugValue]
  ) {
    return false
  }

  showRatingPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/shows/${slugValue}/rating`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating: ratingValue, source: 'P' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return false
    }
    if (!response.ok) {
      return false
    }
    const data = await response.json()
    showRatingStates.value[slugValue] = normalizeRatingValue(data.rating)
    showRatingLoadedBySlug.value[slugValue] = true
    resetCreditShowRatingPreview(credit)
    return true
  } catch {
    return false
  } finally {
    showRatingPendingBySlug.value[slugValue] = false
  }
}

async function setCreditRating(credit, ratingValue) {
  if (selectedMedium.value === 'tv') {
    return setCreditShowRating(credit, ratingValue)
  }
  return setCreditFilmRating(credit, ratingValue)
}

async function removeCreditFilmRating(credit) {
  const slugValue = creditFilmSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'film'
    || !isAuthenticated.value
    || !creditFilmRatingValue(credit)
    || typeof fetch !== 'function'
    || filmRatingPendingBySlug.value[slugValue]
  ) {
    return false
  }

  filmRatingPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/films/${slugValue}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return false
    }
    if (!response.ok) {
      return false
    }
    filmRatingStates.value[slugValue] = null
    filmRatingLoadedBySlug.value[slugValue] = true
    resetCreditFilmRatingPreview(credit)
    return true
  } catch {
    // Leave the current rating unchanged on network or API failure.
    return false
  } finally {
    filmRatingPendingBySlug.value[slugValue] = false
  }
}

async function removeCreditShowRating(credit) {
  const slugValue = creditShowSlug(credit)
  if (
    !slugValue
    || selectedMedium.value !== 'tv'
    || !isAuthenticated.value
    || !creditShowRatingValue(credit)
    || typeof fetch !== 'function'
    || showRatingPendingBySlug.value[slugValue]
  ) {
    return false
  }

  showRatingPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/shows/${slugValue}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return false
    }
    if (!response.ok) {
      return false
    }
    showRatingStates.value[slugValue] = null
    showRatingLoadedBySlug.value[slugValue] = true
    resetCreditShowRatingPreview(credit)
    return true
  } catch {
    return false
  } finally {
    showRatingPendingBySlug.value[slugValue] = false
  }
}

async function removeCreditRating(credit) {
  if (selectedMedium.value === 'tv') {
    return removeCreditShowRating(credit)
  }
  return removeCreditFilmRating(credit)
}

async function removeCompactCreditFilmRating() {
  if (!compactCreditFilmRatingPickerCredit.value) return
  const credit = compactCreditFilmRatingPickerCredit.value
  const wasRemoved = await removeCreditRating(credit)
  if (wasRemoved) {
    closeCompactCreditFilmRatingPicker()
  }
}

async function toggleCreditCalendar(credit) {
  if (selectedMedium.value !== 'tv') {
    return
  }

  const slugValue = creditShowSlug(credit)
  if (
    !slugValue
    || !isAuthenticated.value
    || !isCreditCalendarEnabled(credit)
    || typeof fetch !== 'function'
    || showCalendarPendingBySlug.value[slugValue]
  ) {
    return
  }

  showCalendarPendingBySlug.value[slugValue] = true
  try {
    const response = await fetch(`/api/v1/shows/${slugValue}/calendar`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'P' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      if (response.status === 400) {
        showCalendarStates.value[slugValue] = false
        showCalendarEnabledBySlug.value[slugValue] = false
        showCalendarLoadedBySlug.value[slugValue] = true
      }
      return
    }
    const data = await response.json()
    showCalendarStates.value[slugValue] = Boolean(data.in_calendar)
    showCalendarEnabledBySlug.value[slugValue] = data.enabled !== false
    showCalendarLoadedBySlug.value[slugValue] = true
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    showCalendarPendingBySlug.value[slugValue] = false
  }
}

function creditLoadKey(medium, department) {
  return `${medium}:${department}`
}

function creditsFromPayload(payload, medium, department) {
  if (Array.isArray(payload?.results)) {
    return payload.results
  }
  if (medium === 'tv') {
    return payload?.showography_by_department?.acting || payload?.showography_entries || []
  }
  return payload?.filmography_by_department?.[department] || (
    department === 'acting' ? payload?.filmography_entries || [] : []
  )
}

function creditIdentity(credit, medium) {
  if (medium === 'tv') {
    return creditShowId(credit) || creditShowSlug(credit) || credit?.film_url || credit?.title
  }
  return creditFilmId(credit) || creditFilmSlug(credit) || credit?.film_url || credit?.title
}

function mergeCreditEntries(existingEntries, incomingEntries, medium) {
  const merged = []
  const seen = new Set()
  ;[...existingEntries, ...incomingEntries].forEach((credit) => {
    const key = creditIdentity(credit, medium)
    if (!key || seen.has(key)) {
      return
    }
    seen.add(key)
    merged.push(credit)
  })
  return merged
}

function applySummaryPayload(payload) {
  if (payload?.id) {
    personDatabaseId.value = String(payload.id)
  }
  person.value = {
    ...person.value,
    name: payload?.name || person.value.name,
    jobs: payload?.jobs ?? person.value.jobs,
    bio: payload?.overview ?? '',
  }
  if (payload?.related_resources !== undefined) {
    relatedResources.value = payload.related_resources || []
  }
  filmDepartmentCounts.value = {
    acting: Number(payload?.film_department_counts?.acting || 0),
    directing: Number(payload?.film_department_counts?.directing || 0),
    writing: Number(payload?.film_department_counts?.writing || 0),
    crew: Number(payload?.film_department_counts?.crew || 0),
  }
  showDepartmentCounts.value = {
    acting: Number(payload?.show_department_counts?.acting || 0),
  }
  hasFilmCredits.value = Boolean(
    filmDepartmentCounts.value.acting
      || filmDepartmentCounts.value.directing
      || filmDepartmentCounts.value.writing
      || filmDepartmentCounts.value.crew,
  )
  hasShowCredits.value = Boolean(showDepartmentCounts.value.acting)
  personFavourited.value = Boolean(payload?.person_favourited)
  personFavouriteLoaded.value = payload?.person_favourited !== undefined
  if (payload?.poster_url !== undefined || payload?.background_url !== undefined) {
    heroAssets.value = {
      poster: payload?.poster_url || heroAssets.value.poster || defaultHeroAssets.poster,
      background: payload?.background_url || heroAssets.value.background || defaultHeroAssets.background,
    }
  }
  hasLoadedPersonSummary.value = true
}

function applyCreditsPayload(
  payload,
  medium,
  department,
  loadKey,
  requestedAll = false,
  append = false,
) {
  const entries = creditsFromPayload(payload, medium, department)
  if (medium === 'tv') {
    const existingEntries = showographyByDepartment.value.acting || []
    showographyByDepartment.value = {
      ...showographyByDepartment.value,
      acting: append ? mergeCreditEntries(existingEntries, entries, medium) : entries,
    }
    if (payload?.showography_by_department || payload?.showography_entries) {
      showDepartmentCounts.value = {
        acting: Math.max(showDepartmentCounts.value.acting, entries.length),
      }
    }
  } else {
    const existingEntries = filmographyByDepartment.value[department] || []
    filmographyByDepartment.value = {
      ...filmographyByDepartment.value,
      [department]: append ? mergeCreditEntries(existingEntries, entries, medium) : entries,
    }
    if (payload?.filmography_by_department || payload?.filmography_entries) {
      const legacyDepartments = payload?.filmography_by_department || {}
      const legacyCounts = { ...filmDepartmentCounts.value }
      ;['acting', 'directing', 'writing', 'crew'].forEach((departmentKey) => {
        const departmentEntries = legacyDepartments[departmentKey] || (
          departmentKey === 'acting' ? payload?.filmography_entries || [] : []
        )
        legacyCounts[departmentKey] = Math.max(
          legacyCounts[departmentKey] || 0,
          Array.isArray(departmentEntries) ? departmentEntries.length : 0,
        )
      })
      filmDepartmentCounts.value = {
        ...legacyCounts,
        [department]: Math.max(legacyCounts[department] || 0, entries.length),
      }
    }
  }
  loadedCreditKeys.value = {
    ...loadedCreditKeys.value,
    [loadKey]: true,
  }
  if (requestedAll || payload?.has_next === false) {
    fullyLoadedCreditKeys.value = {
      ...fullyLoadedCreditKeys.value,
      [loadKey]: true,
    }
  }
  applyInitialCreditStatuses(payload)
  loadedCreditMedium.value = medium
}

function creditBatchUrls(medium, department, totalCount, loadedCount) {
  const urls = []
  const batchSize = 20
  for (let offset = loadedCount; offset < totalCount; offset += batchSize) {
    const params = new URLSearchParams({
      medium,
      department,
      offset: String(offset),
      page_size: String(batchSize),
      total_count: String(totalCount),
    })
    urls.push(personApiUrl('credits', params))
  }
  return urls
}

async function loadRemainingCreditBatches(initialPayload, medium, department) {
  const loadKey = creditLoadKey(medium, department)
  if (fullyLoadedCreditKeys.value[loadKey]) {
    return
  }
  const totalCount = Number(initialPayload?.count || 0)
  const loadedCount = Number(initialPayload?.offset || 0) + Number(
    initialPayload?.page_size || initialPayload?.results?.length || 0,
  )
  if (!totalCount || loadedCount >= totalCount) {
    fullyLoadedCreditKeys.value = {
      ...fullyLoadedCreditKeys.value,
      [loadKey]: true,
    }
    return
  }

  const urls = creditBatchUrls(medium, department, totalCount, loadedCount)
  try {
    const responses = await Promise.all(urls.map((url) => fetch(url, {
      headers: buildAuthHeaders(),
    })))
    const payloads = await Promise.all(responses.map((response) => (
      response.ok ? response.json() : null
    )))
    payloads
      .filter(Boolean)
      .sort((left, right) => Number(left.offset || 0) - Number(right.offset || 0))
      .forEach((payload) => {
        applyCreditsPayload(payload, medium, department, loadKey, false, true)
      })
    fullyLoadedCreditKeys.value = {
      ...fullyLoadedCreditKeys.value,
      [loadKey]: true,
    }
    setDepartmentSelection(department)
  } catch {
    // Keep the initially visible credits when background batches fail.
  }
}

async function fetchLegacyPersonDetail(medium) {
  const confirmPageView = capturePageView()
  const response = await fetch(personApiUrl(), {
    headers: {
      ...buildAuthHeaders(),
      'X-Filmsomniac-Medium': medium,
    },
  })
  if (response.status === 404) {
    personNotFound.value = true
    return null
  }
  if (!response.ok) {
    return null
  }
  const payload = await response.json()
  confirmPageView()
  return payload
}

async function finishPersonPayload(payload, medium) {
  applySummaryPayload(payload)
  selectedMedium.value = medium
  const initialDepartment = normalizeDepartmentFromRoute()
  setDepartmentSelection(initialDepartment)
  if (activeSection.value === 'filmography') {
    syncRouteWithDepartment(initialDepartment, true)
    if (payload?.initial_credits) {
      const initialCredits = payload.initial_credits
      const creditMedium = initialCredits.medium || medium
      const creditDepartment = initialCredits.department || initialDepartment
      applyCreditsPayload(
        initialCredits,
        creditMedium,
        creditDepartment,
        creditLoadKey(creditMedium, creditDepartment),
      )
      setDepartmentSelection(creditDepartment)
      hasLoadedPersonPayload.value = true
      loadRemainingCreditBatches(initialCredits, creditMedium, creditDepartment)
    } else if (payload?.filmography_by_department || payload?.showography_by_department) {
      applyCreditsPayload(
        payload,
        medium,
        initialDepartment,
        creditLoadKey(medium, initialDepartment),
        true,
      )
      setDepartmentSelection(initialDepartment)
      hasLoadedPersonPayload.value = true
    } else {
      await loadPersonCredits()
    }
  } else {
    hasLoadedPersonPayload.value = true
  }
  if (isAuthenticated.value && payload?.person_favourited === undefined) {
    loadPersonFavouriteStatus()
  }
  if (activeSection.value === 'lists') {
    await loadPersonListsPage()
  }
}

async function loadPersonCreditSummary(medium, summaryDepartment) {
  const confirmPageView = capturePageView()
  const summaryParams = new URLSearchParams({
    medium,
    department: summaryDepartment,
    initial_credits: '1',
  })
  try {
    const response = await fetch(personApiUrl('summary', summaryParams), {
      headers: buildAuthHeaders(),
    })
    if (response.status === 404) {
      personNotFound.value = true
      return
    }
    if (!response.ok) {
      const legacyPayload = await fetchLegacyPersonDetail(medium)
      if (legacyPayload?.name) {
        await finishPersonPayload(legacyPayload, medium)
      } else {
        await loadPersonCredits({ mediumOverride: medium, departmentOverride: summaryDepartment })
      }
      return
    }
    const payload = await response.json()
    confirmPageView()
    if (!payload?.name && !personDatabaseId.value) {
      const legacyPayload = await fetchLegacyPersonDetail(medium)
      if (legacyPayload?.name) {
        await finishPersonPayload(legacyPayload, medium)
      } else {
        await loadPersonCredits({ mediumOverride: medium, departmentOverride: summaryDepartment })
      }
      return
    }
    await finishPersonPayload(payload, medium)
  } catch {
    await loadPersonCredits({ mediumOverride: medium, departmentOverride: summaryDepartment })
  }
}

async function loadPersonCredits({ all = false, mediumOverride = null, departmentOverride = null } = {}) {
  if (!personId.value || typeof fetch !== 'function') {
    return
  }

  const medium = mediumOverride || routeSectionToMedium(personSection.value)
  if (medium !== 'film' && medium !== 'tv') {
    return
  }
  selectedMedium.value = medium
  const department = medium === 'tv' ? 'acting' : (departmentOverride || normalizeDepartmentFromRoute())
  selectedDepartment.value = department
  const loadKey = creditLoadKey(medium, department)
  if (!all && loadedCreditKeys.value[loadKey]) {
    hasLoadedPersonPayload.value = true
    return
  }
  if (all && fullyLoadedCreditKeys.value[loadKey]) {
    return
  }

  const params = new URLSearchParams({
    medium,
    department,
  })
  if (all) {
    params.set('all', '1')
  }

  try {
    const response = await fetch(personApiUrl('credits', params), {
      headers: buildAuthHeaders(),
    })
    if (response.status === 404) {
      personNotFound.value = true
      return
    }
    if (!response.ok) {
      const legacyPayload = await fetchLegacyPersonDetail(medium)
      if (legacyPayload) {
        applyCreditsPayload(legacyPayload, medium, department, loadKey, true)
        setDepartmentSelection(department)
        hasLoadedPersonPayload.value = true
      }
      return
    }
    const payload = await response.json()
    if (!Array.isArray(payload?.results) && !payload?.filmography_by_department && !payload?.showography_by_department) {
      const legacyPayload = await fetchLegacyPersonDetail(medium)
      if (legacyPayload) {
        applyCreditsPayload(legacyPayload, medium, department, loadKey, true)
        setDepartmentSelection(department)
        hasLoadedPersonPayload.value = true
      }
      return
    }
    applyCreditsPayload(payload, medium, department, loadKey, all)
    setDepartmentSelection(department)
    hasLoadedPersonPayload.value = true
    if (!all) {
      loadRemainingCreditBatches(payload, medium, department)
    }
  } catch {
    // Keep the current credit list if the paged credit request fails.
  }
}

async function loadPersonTitle() {
  const confirmPageView = capturePageView()
  personNotFound.value = false
  if (!personId.value) {
    return
  }

  try {
    hasLoadedPersonSummary.value = false
    hasLoadedPersonPayload.value = false
    personDatabaseId.value = null
    loadedCreditKeys.value = {}
    fullyLoadedCreditKeys.value = {}
    filmographyByDepartment.value = {
      acting: [],
      directing: [],
      writing: [],
      crew: [],
    }
    showographyByDepartment.value = {
      acting: [],
    }
    const medium = routeSectionToMedium(personSection.value)
    const summaryDepartment = medium === 'tv' ? 'acting' : departmentFromSection(personSection.value)
    const response = await fetch(`/api/v1/people/${personId.value}/summary?light=1`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 404) {
      personNotFound.value = true
      return
    }
    if (!response.ok) {
      const payload = await fetchLegacyPersonDetail(medium)
      if (payload) {
        await finishPersonPayload(payload, medium)
      }
      return
    }

    const payload = await response.json()
    confirmPageView()
    if (!payload?.name) {
      loadPersonCreditSummary(medium, summaryDepartment)
      return
    }
    applySummaryPayload(payload)
    selectedMedium.value = medium
    if (activeSection.value === 'filmography') {
      loadPersonCreditSummary(medium, summaryDepartment)
    } else {
      hasLoadedPersonPayload.value = true
      if (activeSection.value === 'lists') {
        loadPersonListsPage()
      }
      if (isAuthenticated.value) {
        loadPersonFavouriteStatus()
      }
    }
  } catch {
    try {
      const medium = routeSectionToMedium(personSection.value)
      const payload = await fetchLegacyPersonDetail(medium)
      if (payload) {
        await finishPersonPayload(payload, medium)
      }
    } catch {
      // Keep the prototype fallback when the detail API is unavailable.
    }
  }
}

onMounted(() => {
  loadPersonTitle()
  window.addEventListener('keydown', handlePersonDetailKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handlePersonDetailKeydown)
})
watch(documentTitle, setDocumentTitle, { immediate: true })
watch(() => heroAssets.value.poster, (src) => {
  resetPersonPortraitLoading(src)
}, { immediate: true })
watch(() => heroAssets.value.background, () => {
  personHeroBackgroundLoaded.value = false
})
watch(personId, () => {
  personFavourited.value = false
  personFavouritePending.value = false
  personFavouriteLoaded.value = false
  loadedCreditMedium.value = null
  loadPersonTitle()
})
watch(personSection, () => {
  if (!hasLoadedPersonSummary.value) {
    return
  }

  const nextMedium = routeSectionToMedium(personSection.value)
  selectedMedium.value = nextMedium
  const nextDepartment = normalizeDepartmentFromRoute()
  if (nextDepartment !== selectedDepartment.value) {
    setDepartmentSelection(nextDepartment)
  }
  if (activeSection.value === 'filmography') {
    hasLoadedPersonPayload.value = false
    loadPersonCredits({ mediumOverride: nextMedium, departmentOverride: nextDepartment })
    syncRouteWithDepartment(nextDepartment, true)
    return
  }

  if (activeSection.value === 'lists') {
    loadPersonListsPage()
  }
})
watch(normalizedFilmographyFilter, (query) => {
  if (query.length < 2 || activeSection.value !== 'filmography') {
    return
  }
  loadPersonCredits({
    all: true,
    mediumOverride: selectedMedium.value,
    departmentOverride: selectedDepartment.value,
  })
})
watch([visibleCredits, isAuthenticated, selectedMedium], ([, authenticated, medium]) => {
  if (medium !== 'film' && medium !== 'tv') {
    return
  }
  if (!authenticated && medium !== 'tv') {
    return
  }
  ensureVisibleActionStatusesLoaded()
}, { immediate: true })
watch([baseCredits, isAuthenticated, selectedMedium], ([, authenticated, medium]) => {
  if (!authenticated) {
    return
  }

  if (medium === 'film') {
    ensureFilmActivityStatusesLoaded()
    return
  }

  if (medium === 'tv') {
    ensureShowActivityStatusesLoaded()
  }
}, { immediate: true })
watch(activityFilterOptions, (options) => {
  if (!options.length) {
    selectedActivity.value = 'all'
    return
  }

  if (!options.some((option) => option.value === selectedActivity.value)) {
    selectedActivity.value = 'all'
  }
}, { immediate: true })
watch(isAuthenticated, (authenticated) => {
  personFavourited.value = false
  personFavouritePending.value = false
  personFavouriteLoaded.value = false
  showCalendarStates.value = {}
  showCalendarPendingBySlug.value = {}
  showCalendarLoadedBySlug.value = {}
  showCalendarEnabledBySlug.value = {}
  compactCreditFilmRatingPickerCredit.value = null

  if (!authenticated) {
    filmWatchedStates.value = {}
    filmWatchedPendingBySlug.value = {}
    filmWatchedLoadedBySlug.value = {}
    filmWatchlistStates.value = {}
    filmWatchlistPendingBySlug.value = {}
    filmWatchlistLoadedBySlug.value = {}
    filmFavouriteStates.value = {}
    filmFavouritePendingBySlug.value = {}
    filmFavouriteLoadedBySlug.value = {}
    filmRatingStates.value = {}
    filmRatingPendingBySlug.value = {}
    filmRatingLoadedBySlug.value = {}
    filmRatingHoverBySlug.value = {}
    showRatingStates.value = {}
    showRatingPendingBySlug.value = {}
    showRatingLoadedBySlug.value = {}
    showRatingHoverBySlug.value = {}
    showWatchlistStates.value = {}
    showWatchlistPendingBySlug.value = {}
    showWatchlistLoadedBySlug.value = {}
    showFavouriteStates.value = {}
    showFavouritePendingBySlug.value = {}
    showFavouriteLoadedBySlug.value = {}
    if (activeSection.value === 'lists') {
      loadPersonListsPage()
    }
    return
  }

  if (activeSection.value === 'lists') {
    loadPersonListsPage()
  }
  selectedActivity.value = 'all'
  loadPersonFavouriteStatus()
  ensureVisibleActionStatusesLoaded()
})

watch([selectedMedium, currentViewMode], ([medium, viewMode]) => {
  if (!['film', 'tv'].includes(medium) || !['1', '3'].includes(viewMode)) {
    closeCompactCreditFilmRatingPicker()
  }
})

useMetaDescription(() => buildMetaDescription(
  'person',
  hasLoadedPersonSummary.value && !personNotFound.value ? { name: person.value.name } : {},
  personSection.value,
))
</script>
